import AgGrid from "../../../common/AgGrid";
import { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from 'xlsx';  
import jsPDF from 'jspdf';              
import autoTable from 'jspdf-autotable';
import SuccessMessage from '../../../common/SuccessMessage';
import { useNavigate, useLocation } from "react-router-dom";
import { getUserTypeID } from "../../../../utils/PermissionUtils";

import {
  GetRevenueReportData,
  GetTrendAnalysisReport,
  ListNameDropdown,
  ListSegment,
} from "../../../../services/accountService";

// ===========================================================================
// COLUMN DEFS
const dateFormatter = (params) => {
  if (!params.value) return "";
  const d = new Date(params.value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const numberFormatter = (params) =>
  params.value != null && params.value !== ""
    ? Number(params.value).toLocaleString()
    : "";

const currencyFormatter = (params) =>
  params.value != null ? `$${Number(params.value).toLocaleString()}` : "";

// ===========================================================================
// ✅ NEW: shared formatter for Client Results / Daily Revenue / Monthly Mail
// Volume export fields — used by BOTH "Export to Excel" and the new
// "Print To Pdf" so the two exports always render identical values
// (mirrors the same field-name heuristic already used inline in
// exportToExcel, just centralized so printToPDF can reuse it).'
// ===========================================================================
const formatVolumeFieldValue = (field, value) => {
  if (value === null || value === undefined || value === "") return "";
  if (field.includes("Date") || ["dropDate", "periodStart", "periodEnd"].includes(field)) {
    return dateFormatter({ value });
  }
  if ([
    "nonDonors", "donors", "pieces", "quantityMailed", "totalMailings",
    "nonDonorsDay", "donorsDay", "nonDonorsCumulative", "donorsCumulative", "daysOut",
  ].includes(field)) {
    return numberFormatter({ value });
  }
  if (["amount", "totalIncome", "amountDay", "amountCumulative"].includes(field)) {
    return currencyFormatter({ value });
  }
  if (field === "percentageTotal") {
    return `${Number(value).toLocaleString()}%`;
  }
  return String(value);   // ← "date" now falls all the way through to here, printed as-is
};

// ✅ NEW: fields that look numeric/currency by name → right-aligned in the PDF table
const isRightAlignVolumeField = (field) => {
  const f = String(field).toLowerCase();
  return (
    f.includes("donor") || f.includes("piece") || f.includes("quantity") ||
    f.includes("mailing") || f.includes("amount") || f.includes("income") ||
    f.includes("percentage") || f.includes("days") || f.includes("cost") ||
    f.includes("net") || f.includes("mailed")
  );
};

// ===========================================================================
// ✅ NEW: TREND ANALYSIS metric definitions — drives both the "Choose Columns"
// popup checkboxes and the dynamically-built per-Job-ID column groups.
// ===========================================================================
const TREND_METRIC_OPTIONS = [
  { key: "totalMailed", label: "Total Mailed", type: "number" },
  { key: "nonDonors", label: "Non Donors", type: "number" },
  { key: "donations", label: "Donations", type: "number" },
  { key: "responseRate", label: "Response Rate", type: "percent" },
  { key: "income", label: "Income", type: "currency" },
  { key: "avgGift", label: "Average Gift", type: "currency" },
  { key: "cost", label: "Cost", type: "currency" },
  { key: "net", label: "Net", type: "currency" },
  // { key: "inOut", label: "In/Out", type: "text" },
];

const formatTrendMetricValue = (value, type) => {
  if (value === null || value === undefined || value === "") return "";
  if (type === "currency") {
    return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (type === "percent") {
    return `${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  if (type === "number") return Number(value).toLocaleString();
  return String(value);
};

// ✅ NEW: small color cue per metric type, used as a dot next to each row in
// the "Choose Columns" popup so number/currency/percent/text are easy to scan
const TREND_METRIC_DOT_COLOR = {
  number: "#2E6F9F",
  currency: "#2f9e5b",
  percent: "#b8860b",
  text: "#8a97a6",
};

// ✅ NEW: one accent color per compared Job ID slot (max 3) — used consistently
// across the Job ID chips/dropdown, the KPI summary cards, and the grid's
// column-group headers so a job is instantly recognizable everywhere.
const TREND_JOB_ACCENT_COLORS = ["#2E6F9F", "#3f8f5f", "#8a5fb8"];


const CustomTooltip = (props) => {
  const { value } = props;

  if (!value) return null;

  return (
    <div
      style={{
        backgroundColor: '#333',
        color: '#fff',
        padding: '8px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        maxWidth: '300px',
        whiteSpace: 'normal',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}
    >
      {value}
    </div>
  );
};



// ===========================================================================
const getClientResultsColumnDefs = (mailCodeCellRenderer) => [
  {
    headerName: "Client ID",
    field: "clientId",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Client Name",
    field: "clientName",
    minWidth: 350,
    filter: 'agTextColumnFilter',
    tooltipField: 'clientName',          // ✅ VALUE
    tooltipComponent: 'customTooltip',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Mail Code",
    field: "mailCode",
    minWidth: 250,
    filter: 'agTextColumnFilter',
    cellRenderer: mailCodeCellRenderer,
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Date",
    field: "date",
    minWidth: 250,
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    filter: 'agTextColumnFilter',
    // valueFormatter: dateFormatter,
    valueFormatter: (params) => (params.value != null ? String(params.value) : ""),
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const formattedValue = params.valueFormatted ?? params.value;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
      }
      return formattedValue;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Day of Week",
    field: "dayOfWeek",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Non Donors",
    field: "nonDonors",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Donors",
    field: "donors",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Pieces",
    field: "pieces",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Amount",
    field: "amount",
    minWidth: 240,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null ? `$${Number(params.value).toLocaleString()}` : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    },
  },
  {
    headerName: "Period Start",
    field: "periodStart",
    filter: 'agTextColumnFilter',
    minWidth: 240,
    valueFormatter: dateFormatter,
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const formattedValue = params.valueFormatted ?? params.value;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
      }
      return formattedValue;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Period End",
    field: "periodEnd",
    filter: 'agTextColumnFilter',
    minWidth: 240,
    valueFormatter: dateFormatter,
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const formattedValue = params.valueFormatted ?? params.value;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
      }
      return formattedValue;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
];

const getMonthlyMailVolumeColumnDefs = (jobIdCellRenderer) => [
  {
    headerName: "Client ID",
    field: "clientID",
    minWidth: 230,

    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Client Name",
    field: "clientName",
    minWidth: 350,
    filter: "agTextColumnFilter",
    tooltipField: 'clientName',          // ✅ VALUE
    tooltipComponent: 'customTooltip',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Drop Date",
    field: "dropDate",
    minWidth: 250,
    filter: "agTextColumnFilter",
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    valueFormatter: dateFormatter,
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const formattedValue = params.valueFormatted ?? params.value;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
      }
      return formattedValue;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Month",
    field: "month",
    minWidth: 200,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Job ID",
    field: "jobID",
    minWidth: 230,
    filter: "agTextColumnFilter",
    cellRenderer: jobIdCellRenderer,
  },
  {
    headerName: "Job Name",
    field: "jobName",
    minWidth: 350,
    filter: "agTextColumnFilter",
    tooltipField: 'jobName',          // ✅ VALUE
    tooltipComponent: 'customTooltip',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Quantity Mailed",
    field: "quantityMailed",
    minWidth: 200,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    valueFormatter: (p) => (p.value ? Number(p.value).toLocaleString() : ""),
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Job Type",
    field: "jobType",
    minWidth: 200,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Total Income",
    field: "totalIncome",
    minWidth: 200,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? `$${Number(params.value).toLocaleString()}` : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    valueFormatter: (p) => (p.value ? `$${Number(p.value).toLocaleString()}` : ""),
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Total Mailings",
    field: "totalMailings",
    minWidth: 250,
    filter: "agTextColumnFilter",
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    valueFormatter: (p) => (p.value ? Number(p.value).toLocaleString() : ""),
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
];

// ===========================================================================
// ✅ NEW: DAILY REVENUE COLUMN DEFS (per "Revenue Report" UI spec)
// Filters for this report are: Client(s), Job ID, Tallies Date Range, Period
// (Daily / Weekly / Quarterly). The columns below mirror the "FIELDS" list
// from the design — Job ID itself is only a filter, not a displayed column.
//
// ✅ FIX (Revenue Report only — item 1): "Amount Cumulative" and
// "Total Income" are running PER-ROW cumulative values. Re-summing them
// on the Grand Total (pinned bottom) row produces a misleading number, so
// that row now renders blank for these two columns instead of "$0.00".
// ===========================================================================
const getDailyRevenueColumnDefs = () => [
  {
    headerName: "Client ID",
    field: "clientID",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Client Name",
    field: "clientName",
    minWidth: 320,
    filter: 'agTextColumnFilter',
    tooltipField: 'clientName',
    tooltipComponent: 'customTooltip',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Period",
    field: "period",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
      }
      return params.value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Date",
    field: "date",
    minWidth: 250,
    filter: 'agTextColumnFilter',
      comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
    // valueFormatter: dateFormatter,
     valueFormatter: (params) => (params.value != null ? String(params.value) : ""), 
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const formattedValue = params.valueFormatted ?? params.value;
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{formattedValue}</span>;
      }
      return formattedValue;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Non-Donors (Day)",
    field: "nonDonorsDay",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null && params.value !== "" ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Donors (Day)",
    field: "donorsDay",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null && params.value !== "" ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Amount (Day)",
    field: "amountDay",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null ? `$${Number(params.value).toLocaleString()}` : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Non-Donors Cumulative",
    field: "nonDonorsCumulative",
    minWidth: 230,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null && params.value !== "" ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Donors Cumulative",
    field: "donorsCumulative",
    minWidth: 220,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null && params.value !== "" ? Number(params.value).toLocaleString() : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Amount Cumulative",
    field: "amountCumulative",
    minWidth: 220,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      // ✅ FIX (item 1 — Revenue Report only): running per-row cumulative
      // value — Grand Total row shows blank instead of re-summing to "$0.00"
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}></span>;
      }
      const value = params.value != null ? `$${Number(params.value).toLocaleString()}` : "";
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  {
    headerName: "Total Income",
    field: "totalIncome",
    minWidth: 200,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      // ✅ FIX (item 1 — Revenue Report only): running per-row cumulative
      // value — Grand Total row shows blank instead of re-summing to "$0.00"
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}></span>;
      }
      const value = params.value != null ? `$${Number(params.value).toLocaleString()}` : "";
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
  // {
  //   headerName: "Days Out",
  //   field: "daysOut",
  //   minWidth: 160,
  //   filter: 'agTextColumnFilter',
  //   cellRenderer: (params) => {
  //     const isTotal = params.data?.isTotalsRow;
  //     const value = params.value != null && params.value !== "" ? Number(params.value).toLocaleString() : "";
  //     if (isTotal) {
  //       return <span style={{ fontWeight: 'bold' }}>{value}</span>;
  //     }
  //     return value;
  //   },
  //   cellStyle: (params) => {
  //     if (params.data?.isTotalsRow) {
  //       return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
  //     }
  //     return { textAlign: 'right' };
  //   }
  // },
  {
    headerName: "Percentage Total",
    field: "percentageTotal",
    minWidth: 250,
    filter: 'agTextColumnFilter',
    cellRenderer: (params) => {
      const isTotal = params.data?.isTotalsRow;
      const value = params.value != null && params.value !== "" ? `${Number(params.value).toLocaleString()}%` : "";
      if (isTotal) {
        return <span style={{ fontWeight: 'bold' }}>{value}</span>;
      }
      return value;
    },
    cellStyle: (params) => {
      if (params.data?.isTotalsRow) {
        return { backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
      }
      return { textAlign: 'right' };
    }
  },
];


const tooltipCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

// ===========================================================================
// TREND ANALYSIS COLUMN DEFS — "List" + "list_sel" always shown, then one
// FLAT column per selected Job ID × metric, colored per job. The job code
// is shown as a first "line" of header text only on that job's first
// column (via a literal \n + CSS white-space:pre-line), so it visually
// reads like a merged header spanning the group — without ever touching
// ag-grid's actual column-group mechanism, which has a rendering bug on
// this setup where the group sub-header row disappears once 2+ groups
// exist. This flat approach uses the same proven column pattern already
// working reliably on every other report tab, so it can't hit that bug.
// jobLabels: { [jobID]: "B-800H" } — used as the header's job-code line.
// ===========================================================================
const getTrendAnalysisColumnDefs = (jobIds, selectedColumns, jobLabels) => {
  const baseCols = [
    {
      headerName: "List",
      field: "list",
      minWidth: 180,
      pinned: 'left',
      filter: 'agTextColumnFilter',
      tooltipField: 'list',
      tooltipComponent: CustomTooltip,
      cellRenderer: (params) => {
        const isTotal = params.data?.isTotalsRow;
        if (isTotal) return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
        return params.value;
      },
      cellStyle: (params) => params.data?.isTotalsRow
        ? { ...tooltipCellStyle, backgroundColor: '#f0f8ff', textAlign: 'left', fontWeight: 'bold' }
        : { ...tooltipCellStyle, textAlign: 'left' },
    },
    {
      headerName: "list_sel",
      field: "listSel",
      minWidth: 220,
      pinned: 'left',
      filter: 'agTextColumnFilter',
      tooltipField: 'listSel',
      tooltipComponent: CustomTooltip,
      cellRenderer: (params) => {
        const isTotal = params.data?.isTotalsRow;
        if (isTotal) return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
        return params.value;
      },
      cellStyle: (params) => params.data?.isTotalsRow
        ? { ...tooltipCellStyle, backgroundColor: '#f0f8ff', textAlign: 'left', fontWeight: 'bold' }
        : { ...tooltipCellStyle, textAlign: 'left' },
    },
  ];

  const activeMetrics = TREND_METRIC_OPTIONS.filter((m) => selectedColumns[m.key]);

  const jobCols = (jobIds || []).flatMap((jobId, idx) => {
    const jobCode = jobLabels?.[jobId] || jobId;
    const accentClass = `vt-job-group-${idx % 3}`;
    return activeMetrics.map((metric, metricIdx) => ({
      colId: `${jobId}__${metric.key}`,
      // ✅ Two-line header text via a literal newline + CSS white-space:
      // pre-line — job code only on the first column of each job (so it
      // reads like one merged label spanning the group), metric name always.
      headerName: `${metricIdx === 0 ? jobCode : '\u00A0'}\n${metric.label}`,
      field: `${jobId}__${metric.key}`,
      headerClass: accentClass,
      minWidth: 185,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        const isTotal = params.data?.isTotalsRow;
        const value = formatTrendMetricValue(params.value, metric.type);
        if (isTotal) return <span style={{ fontWeight: 'bold' }}>{value}</span>;
        return value;
      },
      cellStyle: (params) => params.data?.isTotalsRow
        ? { backgroundColor: '#f0f8ff', textAlign: 'right' }
        : { textAlign: 'right' },
    }));
  });

  return [...baseCols, ...jobCols];
};


// ===========================================================================
const VolumeTrackingTab = ({
  styles,
  clients,
  volumeTrackingType,
  setVolumeTrackingType,
  searchText,
  setSearchText,
  onGridReady,
  clientResultsData,
  clientResultFilters,
  setClientResultFilters,
  fetchClientResults,
  handleClearClientResults,
  monthlyMailVolumeDataState,
  monthlyMailVolumeFilters,
  setMonthlyMailVolumeFilters,
  fetchMonthlyMailVolume,
  handleClearMonthlyMailVolume,
  jobDetailsJobId,
  fetchJobsByClient,

  // ✅ Daily Revenue report — this tab now calls the real API
  // (GetRevenueReportData) directly on "Run Report", so it no longer needs
  // rowData handed down as a prop. fetchDailyRevenue/handleClearDailyRevenue
  // stay as optional hooks in case the parent wants to know when this runs.
  fetchDailyRevenue = () => { },
  handleClearDailyRevenue = () => { },

  // ✅ Trend Analysis report — like Daily Revenue, this tab now calls the
  // real APIs directly (GetTrendAnalysisReport, ListNameDropdown, ListSegment)
  // on "Run Report" / dropdown interaction, so it no longer needs rowData,
  // totals, or dropdown options handed down as props. fetchTrendAnalysis /
  // handleClearTrendAnalysis stay as optional hooks in case the parent wants
  // to know when this runs.
  fetchTrendAnalysis = () => { },
  handleClearTrendAnalysis = () => { },

}) => {
  const isClientResults = volumeTrackingType === "Client Results";
  const isDailyRevenue = volumeTrackingType === "Daily Revenue";       // ✅ NEW
  const isTrendAnalysis = volumeTrackingType === "Trend Analysis";     // ✅ NEW

  const userTypeID = getUserTypeID();
  const isClientOrConsultant = userTypeID === 2 || userTypeID === 3;

  const [filteredData, setFilteredData] = useState([]);

  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  // -- Job ID multiselect (Monthly Mail Volume) --
  const [jobSearch, setJobSearch] = useState("");
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const jobDropdownRef = useRef(null);
  const restoringRef = useRef(false);

  // -- Job ID multiselect (Daily Revenue) -- ✅ NEW
  const [revenueJobSearch, setRevenueJobSearch] = useState("");
  const [showRevenueJobDropdown, setShowRevenueJobDropdown] = useState(false);
  const revenueJobDropdownRef = useRef(null);

  // ✅ FIX: this used to arrive as a prop with a no-op default setter, so
  // every setDailyRevenueFilters(...) call (including job checkbox clicks)
  // was silently doing nothing. Making it real local state fixes that.
  const [dailyRevenueFilters, setDailyRevenueFilters] = useState({
    fromDate: "",
    toDate: "",
    clientIds: [],
    jobIDs: [],
    period: "Daily",
  });

  // ✅ NEW: real API-backed data for Daily Revenue (GetRevenueReportData)
  const [dailyRevenueRows, setDailyRevenueRows] = useState([]);
  const [dailyRevenueLoading, setDailyRevenueLoading] = useState(false);

  // -- Job ID multiselect (Trend Analysis) — capped at 3 selections -- ✅ NEW
  const [trendJobSearch, setTrendJobSearch] = useState("");
  const [showTrendJobDropdown, setShowTrendJobDropdown] = useState(false);
  const trendJobDropdownRef = useRef(null);
  const MAX_TREND_JOBS = 3;

  // -- "Choose Columns" popup (Trend Analysis) -- ✅ NEW
  const [showColumnsPopup, setShowColumnsPopup] = useState(false);
  const columnsPopupRef = useRef(null);

  // Local filter state for Trend Analysis (kept local for the same reason
  // dailyRevenueFilters is local — no parent wiring needed for the UI to work)
  const [trendFilters, setTrendFilters] = useState({
    fromDate: "",
    toDate: "",
    clientIds: [],
    jobIds: [],
    list: "",
    listSel: "",
  });

  // Which metric columns are currently checked in "Choose Columns"
  const [selectedTrendColumns, setSelectedTrendColumns] = useState({
    totalMailed: true,
    nonDonors: true,
    donations: true,
    responseRate: true,
    income: true,
    avgGift: true,
    cost: true,
    net: true,
    inOut: false,
  });

  // ✅ NEW: real API-backed data for Trend Analysis (GetTrendAnalysisReport)
  // trendRows keeps the same { list, listSel, metrics: { [jobID]: {...} } }
  // shape the rest of the component already expects.
  const [trendRows, setTrendRows] = useState([]);
  const [trendTotals, setTrendTotals] = useState({});
  const [trendLoading, setTrendLoading] = useState(false);

  // ✅ NEW: "List" dropdown options (ListNameDropdown) + "Select" (list_sel)
  // dropdown options (ListSegment), which depend on the chosen List.
  const [listNameOptions, setListNameOptions] = useState([]);
  const [listSegmentOptions, setListSegmentOptions] = useState([]);
  const [selectedListMasterId, setSelectedListMasterId] = useState(null);
  const [listSegmentLoading, setListSegmentLoading] = useState(false);



  const [showFilters, setShowFilters] = useState(true);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);  // ← ADDED
  const menuDropdownRef = useRef(null);  // ← ADDED

  // ✅ NEW: ref to AgGrid — gives us access to the grid's live column
  // state (getVisibleColumns) at export time. This is the same pattern
  // ListAnalysisTab already uses: if the user drags a column out of the
  // grid or hides it via the column menu, Export to Excel now respects
  // that instead of always dumping every possible column.
  const agGridRef = useRef(null);

  // ✅ NEW: raw AG Grid api (captured in onGridReady below) — used to read
  // checked/selected rows and the live filtered set at export time, same
  // pattern as JobReportsTab's / ListAnalysisTab's gridApiRef.
  const gridApiRef = useRef(null);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const toggleFilters = () => setShowFilters(!showFilters);
  const toggleMenuDropdown = () => setShowMenuDropdown(!showMenuDropdown);  // ← ADDED

  const navigate = useNavigate();
  const location = useLocation();

  // ✅ NEW: load "List" dropdown options the first time the Trend Analysis
  // tab is opened (cached afterwards — list names don't change per session).
  useEffect(() => {
    if (!isTrendAnalysis || listNameOptions.length > 0) return;
    (async () => {
      try {
        const data = await ListNameDropdown();
        setListNameOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching list name dropdown:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrendAnalysis]);

  // ✅ NEW: when the user picks a List, fetch its segments for the "Select" dropdown
  const handleTrendListChange = async (e) => {
    const selectedName = e.target.value;
    setTrendFilters((prev) => ({ ...prev, list: selectedName, listSel: "" }));
    setListSegmentOptions([]);

    if (!selectedName) {
      setSelectedListMasterId(null);
      return;
    }

    const selectedOption = listNameOptions.find((l) => l.listName === selectedName);
    const masterId = selectedOption ? selectedOption.listMasterID : null;
    setSelectedListMasterId(masterId);

    if (!masterId) return;

    setListSegmentLoading(true);
    try {
      const data = await ListSegment(masterId);
      setListSegmentOptions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching list segments:', error);
      setListSegmentOptions([]);
    } finally {
      setListSegmentLoading(false);
    }
  };

  const jobIdCellRenderer = (params) => {
    if (!params.value || params.data?.isTotalsRow) {
      return params.value;
    }

    const jobCode = params.value;

    return (
      <span
        onClick={() =>
          navigate(`/job-details/${jobCode}`, {
            state: {
              source: "reports",
              fromReports: true,
              restoreReports: true,

              reportType: volumeTrackingType,

              reportFilters:
                volumeTrackingType === "Client Results"
                  ? clientResultFilters
                  : monthlyMailVolumeFilters,

              rowData:
                volumeTrackingType === "Client Results"
                  ? clientResultsData
                  : monthlyMailVolumeDataState,

              selectedClients,
              selectedClientIds,

              searchText,
            },
          })
        }
        style={{
          color: "#2563eb",
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {jobCode}
      </span>
    );
  };

  // ✅ NEW: clickable/underlined Mail Code — navigates to Tallies "Successful Batches"
  //    carrying ALL batch numbers tied to this row. Backend sends batchNumber as
  //    a comma-separated string (e.g. "BHY4343, BNMVP8903, TEST0207") when a
  //    mail code spans multiple batches.
  const mailCodeCellRenderer = (params) => {
    const isTotal = params.data?.isTotalsRow;
    if (isTotal) {
      return <span style={{ fontWeight: 'bold' }}>{params.value}</span>;
    }

    // Agar mail code hi khaali hai to plain render karo
    if (!params.value) return params.value;

    return (
      <span
        onClick={(e) => {
          e.stopPropagation();

          const rawBatchNumber = params.data?.batchNumber;
          let batchNumbers = [];

          if (rawBatchNumber !== null && rawBatchNumber !== undefined && rawBatchNumber !== '') {
            batchNumbers = String(rawBatchNumber)
              .split(',')
              .map(b => b.trim())
              .filter(Boolean);
          } else {
            // ✅ Fallback: batchNumber missing ho to mailCode se hi search try karo
            batchNumbers = [params.data.mailCode];
          }

          navigate('/talliesSection', {
            state: {
              batchNumbers,
              mailCode: params.data.mailCode,
              source: 'reports',
              reportType: 'Client Results',
              reportFilters: clientResultFilters,
              rowData: clientResultsData,
              selectedClients,
              selectedClientIds,
              searchText,
            }
          });
        }}
        style={{
          color: '#2563eb',
          cursor: 'pointer',
          textDecoration: 'underline'
        }}
      >
        {params.value}
      </span>
    );
  };

  useEffect(() => {
    if (location.state?.restoreReports) {
      restoringRef.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    if (
      !location.state?.restoreReports ||
      (location.state?.reportType !== "Monthly Mail Volume" &&
        location.state?.reportType !== "Client Results")
    ) {
      return;
    }

    if (location.state.selectedClients) {
      setSelectedClients(location.state.selectedClients);
    }

    if (location.state.selectedClientIds) {
      setSelectedClientIds(location.state.selectedClientIds);
    }

  }, [location.state]);

  useEffect(() => {
    if (!clients?.length) return;

    const ids = monthlyMailVolumeFilters?.clientIds || [];

    if (!ids.length) return;

    const selected = clients.filter(c =>
      ids.includes(c.clientID)
    );

    setSelectedClients(selected);
    setSelectedClientIds(ids);

  }, [clients, monthlyMailVolumeFilters?.clientIds]);

  useEffect(() => {
    const ids = monthlyMailVolumeFilters?.clientIds || [];

    if (ids.length) {
      fetchJobsByClient(ids);
    }
  }, [monthlyMailVolumeFilters?.clientIds]);

  // ✅ NEW: mirror the same "restore jobs for selected clients" behaviour for
  // the Daily Revenue report's own filter object.
  useEffect(() => {
    const ids = dailyRevenueFilters?.clientIds || [];

    if (ids.length) {
      fetchJobsByClient(ids);
    }
  }, [dailyRevenueFilters?.clientIds]);

  // ✅ NEW: same for Trend Analysis's own filter object
  useEffect(() => {
    const ids = trendFilters?.clientIds || [];

    if (ids.length) {
      fetchJobsByClient(ids);
    }
  }, [trendFilters?.clientIds]);


  const [totals, setTotals] = useState({
    totalDonors: 0,
    totalNonDonors: 0,
    totalPieces: 0,
    totalAmount: 0,
  });

  const parseNumber = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const num = Number(String(val).replace(/[$,%]/g, ""));
    return isNaN(num) ? 0 : num;
  };


  useEffect(() => {
    // reset job selection when client changes
    setMonthlyMailVolumeFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: also reset Daily Revenue's job selection when client changes
    setDailyRevenueFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: also reset Trend Analysis's job comparison when client changes
    setTrendFilters((p) => ({
      ...p,
      jobIds: [],
    }));
  }, [selectedClientIds]);

  const calculateTotals = (data) => {
    let totalDonors = 0;
    let totalNonDonors = 0;
    let totalPieces = 0;
    let totalAmount = 0;
    let totalMailings = 0;

    data.forEach((row) => {
      if (row?.isTotalsRow) return;

      if (volumeTrackingType === "Client Results") {
        totalDonors += parseNumber(row.donors);
        totalNonDonors += parseNumber(row.nonDonors);
        totalPieces += parseNumber(row.pieces);
        totalAmount += parseNumber(row.amount);
      } else if (volumeTrackingType === "Daily Revenue") {
        // ✅ NEW: totals for the Daily Revenue grid
        totalDonors += parseNumber(row.donorsDay);
        totalNonDonors += parseNumber(row.nonDonorsDay);
        totalAmount += parseNumber(row.amountDay);
      } else {
        totalPieces += parseNumber(row.quantityMailed);
        totalAmount += parseNumber(row.totalIncome);
        totalMailings += parseNumber(row.totalMailings);
      }
    });

    return { totalDonors, totalNonDonors, totalPieces, totalAmount, totalMailings };
  };
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm.trim()) return clients;
    return clients.filter((c) =>
      c.clientName.toLowerCase().includes(clientSearchTerm.toLowerCase())
    );
  }, [clients, clientSearchTerm]);

  // Filtered job list for the Job ID multiselect (matches by jobCode)
  const filteredJobs = useMemo(() => {
    const list = jobDetailsJobId || [];
    if (!jobSearch.trim()) return list;
    return list.filter((j) =>
      String(j.jobCode ?? "").toLowerCase().includes(jobSearch.toLowerCase())
    );
  }, [jobDetailsJobId, jobSearch]);

  // ✅ NEW: Filtered job list for the Daily Revenue Job ID multiselect
  const filteredRevenueJobs = useMemo(() => {
    const list = jobDetailsJobId || [];
    if (!revenueJobSearch.trim()) return list;
    return list.filter((j) =>
      String(j.jobCode ?? "").toLowerCase().includes(revenueJobSearch.toLowerCase())
    );
  }, [jobDetailsJobId, revenueJobSearch]);

  // ✅ NEW: Filtered job list for the Trend Analysis Job ID multiselect
  const filteredTrendJobs = useMemo(() => {
    const list = jobDetailsJobId || [];
    if (!trendJobSearch.trim()) return list;
    return list.filter((j) =>
      String(j.jobCode ?? "").toLowerCase().includes(trendJobSearch.toLowerCase())
    );
  }, [jobDetailsJobId, trendJobSearch]);

  // ✅ NEW: jobID -> jobCode lookup, used as the comparison column group headers
  const jobLabelMap = useMemo(() => {
    const map = {};
    (jobDetailsJobId || []).forEach((j) => { map[j.jobID] = j.jobCode; });
    return map;
  }, [jobDetailsJobId]);

  // selected job IDs live in monthlyMailVolumeFilters.jobIDs (array)
  const selectedJobIDs = Array.isArray(monthlyMailVolumeFilters.jobIDs)
    ? monthlyMailVolumeFilters.jobIDs
    : [];

  // ✅ NEW: selected job IDs for Daily Revenue live in dailyRevenueFilters.jobIDs
  const selectedRevenueJobIDs = Array.isArray(dailyRevenueFilters.jobIDs)
    ? dailyRevenueFilters.jobIDs
    : [];

  // ✅ NEW: selected job IDs for Trend Analysis — capped at MAX_TREND_JOBS
  const selectedTrendJobIds = Array.isArray(trendFilters.jobIds)
    ? trendFilters.jobIds
    : [];

  // ✅ NEW: toggle a job in the Trend Analysis comparison, enforcing the max-3 cap
  const handleTrendJobToggle = (jobId) => {
    setTrendFilters((prev) => {
      const current = Array.isArray(prev.jobIds) ? prev.jobIds : [];
      if (current.includes(jobId)) {
        return { ...prev, jobIds: current.filter((id) => id !== jobId) };
      }
      if (current.length >= MAX_TREND_JOBS) {
        setMessage(`You can only compare up to ${MAX_TREND_JOBS} jobs at a time`);
        setMessageType('error');
        return prev;
      }
      return { ...prev, jobIds: [...current, jobId] };
    });
  };

  // ✅ NEW: flatten trendRows (grouped by List/list_sel with a per-job
  // metrics object) into the flat field-per-column shape AgGrid needs
  const trendRowData = useMemo(() => {
    return (trendRows || []).map((row) => {
      const flat = { list: row.list, listSel: row.listSel };
      selectedTrendJobIds.forEach((jobId) => {
        const m = row.metrics?.[jobId] || {};
        TREND_METRIC_OPTIONS.forEach(({ key }) => {
          flat[`${jobId}__${key}`] = m[key];
        });
      });
      return flat;
    });
  }, [trendRows, selectedTrendJobIds]);

  // ✅ NEW: Grand Total pinned row for Trend Analysis, built from trendTotals
  const trendTotalsRow = useMemo(() => {
    const row = { isTotalsRow: true, list: "", listSel: "TOTAL" };
    selectedTrendJobIds.forEach((jobId) => {
      const m = trendTotals?.[jobId] || {};
      TREND_METRIC_OPTIONS.forEach(({ key }) => {
        row[`${jobId}__${key}`] = m[key];
      });
    });
    return row;
  }, [trendTotals, selectedTrendJobIds]);


  // ✅ NEW: single source of truth for "what data/columns are we showing right now"
  const currentRowData = isClientResults
    ? clientResultsData
    : isDailyRevenue
      ? dailyRevenueRows
      : isTrendAnalysis
        ? trendRowData
        : monthlyMailVolumeDataState;

  // ✅ NEW: Only export columns currently VISIBLE in the grid — mirrors
  // ListAnalysisTab's getExportColumnsFor(). If the user hides a column
  // via the column menu or drags it out of the grid, it's excluded from
  // "Export to Excel" too, instead of Excel always containing every
  // possible column regardless of what's actually shown on screen.
  // Falls back to the full static column list only if the grid API
  // genuinely isn't ready yet.
  const getExportColumnsFor = (columnDefs) => {
    const fallback = columnDefs
      .filter((col) => col.field && col.field !== 'action')
      .map((col) => ({ field: col.field, headerName: col.headerName || col.field }));

    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) {
        return visible.filter((c) => c.field && c.field !== 'action');
      }
    }
    return fallback;
  };

  // ✅ NEW: row-selection scope for exports — mirrors JobReportsTab's /
  // ListAnalysisTab's getExportRows exactly: checked rows (that are still
  // visible under the current filter) win first, then whatever the
  // grid's active filter is currently showing, then everything. This is
  // what makes the new "select all" checkbox column actually do
  // something on Export to Excel / Print To Pdf, instead of just being
  // decorative.
  const getExportRows = (baseData) => {
    const allRows = (baseData || []).filter((r) => !r?.isTotalsRow);
    const api = gridApiRef.current;

    let visible = null;
    if (api && typeof api.forEachNodeAfterFilter === 'function') {
      const rows = [];
      api.forEachNodeAfterFilter((node) => {
        if (node.data && !node.data.isTotalsRow) rows.push(node.data);
      });
      visible = rows;
    } else if (filteredData.length > 0) {
      // Fallback to the filter snapshot this tab already tracks via
      // handleFilterChanged, in case the live grid api isn't available.
      visible = filteredData.filter((r) => !r?.isTotalsRow);
    }

    const isFiltered = visible !== null && visible.length !== allRows.length;

    let selected = [];
    if (api && typeof api.getSelectedRows === 'function') {
      selected = api.getSelectedRows().filter((r) => r && !r.isTotalsRow);
    }

    if (selected.length > 0) {
      if (visible !== null) {
        const visibleSet = new Set(visible);
        const checkedVisible = selected.filter((r) => visibleSet.has(r));
        if (checkedVisible.length > 0) {
          return { rows: checkedVisible, scope: 'selected' };
        }
      } else {
        return { rows: selected, scope: 'selected' };
      }
    }

    if (isFiltered) {
      return { rows: visible, scope: 'filtered' };
    }

    if (allRows.length === 0) {
      return { rows: [], scope: 'all' };
    }
    return { rows: visible !== null ? visible : allRows, scope: 'all' };
  };

  // ✅ FIX: dedicated, collision-free column builder for Trend Analysis
  // exports (Excel + PDF both use this — single source of truth).
  //
  // Root cause of the bug where "job 2" only exported its Total Mailed
  // column and every other metric was missing: the ON-SCREEN grid header
  // deliberately reuses a blank line (NBSP) instead of the job code for
  // every metric column AFTER a job's first one, purely so it *visually*
  // reads like one merged group heading (see getTrendAnalysisColumnDefs
  // above). That's fine for rendering, but Export to Excel keyed each
  // row by that raw header TEXT — so "Non Donors" / "Donations" / etc.
  // for job 1 and job 2 produced the exact same header string and
  // collided, with only the last-processed job's value surviving per
  // column. Only each job's first metric (which does include the job
  // code, e.g. "C-202HB") stayed unique, which is exactly the symptom
  // reported: only Total Mailed showed up per extra job.
  //
  // Fix: build export headers independently from the grid's display
  // trick, always as "{jobCode} - {metric label}" (guaranteed unique per
  // job), and always include List / List Select. This also sidesteps
  // relying on the grid's live column state for a pinned-column setup,
  // which is what was silently dropping List / List Select from the
  // export entirely.
  const buildTrendExportColumns = () => {
    const allCols = [
      { field: 'list', headerName: 'List' },
      { field: 'listSel', headerName: 'List Select' },
    ];
    selectedTrendJobIds.forEach((jobId) => {
      const jobCode = jobLabelMap[jobId] || jobId;
      TREND_METRIC_OPTIONS.forEach((metric) => {
        if (!selectedTrendColumns[metric.key]) return;
        allCols.push({
          field: `${jobId}__${metric.key}`,
          headerName: `${jobCode} - ${metric.label}`,
        });
      });
    });

    // ✅ FIX: also respect any column the user hid/dragged OUT of the grid
    // directly (via the grid's own column menu), the same way the other
    // three report types already do. Previously this only checked the
    // "Choose Columns" popup state, so a column removed straight from the
    // grid (e.g. "Total Mailed" / "Non Donors" on job 1) still showed up
    // in Export to Excel even though it was no longer visible on screen.
    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) {
        const visibleFields = new Set(visible.map((c) => c.field));
        return allCols.filter((c) => visibleFields.has(c.field));
      }
    }
    return allCols;
  };

  // ✅ Shared totals-row lookup for Client Results / Daily Revenue /
  // Monthly Mail Volume — used by BOTH Export to Excel and Print To Pdf
  // so the TOTAL row always matches between the two.
  //
  // ✅ FIX (item 3 — Revenue Report only): the Daily Revenue branch now
  // takes the ACTUAL rows being exported (`rowsForTotal` — the same
  // checked/filtered/all rows getExportRows() already resolved) and sums
  // them itself, instead of reading the tab-wide `totals` state. The old
  // `totals` state only ever tracked the grid's active FILTER, never
  // row-selection checkboxes, so checking a subset of rows and exporting
  // still silently totaled every row in the report. Client Results /
  // Monthly Mail Volume are untouched — this file only requested the
  // fix for Revenue Report.
  const getVolumeTotalValueForField = (field, rowsForTotal) => {
    if (isClientResults) {
      if (field === 'donors') return numberFormatter({ value: totals.totalDonors });
      if (field === 'nonDonors') return numberFormatter({ value: totals.totalNonDonors });
      if (field === 'pieces') return numberFormatter({ value: totals.totalPieces });
      if (field === 'amount') return currencyFormatter({ value: totals.totalAmount });
      return '';
    }
    if (isDailyRevenue) {
      // ✅ FIX (Revenue Report only): total the EXACT rows being exported
      // (checked rows > active filter > all rows — same priority order as
      // getExportRows), not the tab-wide `totals` state.
      const rows = (rowsForTotal || []).filter((r) => !r?.isTotalsRow);
      let sumNonDonors = 0, sumDonors = 0, sumAmount = 0;
      rows.forEach((row) => {
        sumNonDonors += parseNumber(row.nonDonorsDay);
        sumDonors += parseNumber(row.donorsDay);
        sumAmount += parseNumber(row.amountDay);
      });
      if (field === 'nonDonorsDay') return numberFormatter({ value: sumNonDonors });
      if (field === 'donorsDay') return numberFormatter({ value: sumDonors });
      if (field === 'amountDay') return currencyFormatter({ value: sumAmount });
      // Amount Cumulative / Total Income are running per-row cumulative
      // values (see item 1 above) — never show a re-summed Grand Total.
      return '';
    }
    // Monthly Mail Volume
    if (field === 'quantityMailed') return numberFormatter({ value: totals.totalPieces });
    if (field === 'totalIncome') return currencyFormatter({ value: totals.totalAmount });
    if (field === 'totalMailings') return numberFormatter({ value: totals.totalMailings });
    return '';
  };

  // ✅ EXPORT TO EXCEL FUNCTION - ADDED
  const exportToExcel = () => {
    if (isTrendAnalysis) {
      // ✅ NEW: Trend Analysis export — flatten List/list_sel + per-job metrics
      const { rows: trendExportRows } = getExportRows(trendRowData);
      if (!trendExportRows || trendExportRows.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }
      try {
        // ✅ FIX: guaranteed-unique headers per job/metric — see
        // buildTrendExportColumns() above for why the old approach
        // (grid's live headerName) silently dropped job 2's columns.
        const flatCols = buildTrendExportColumns();

        const exportData = trendExportRows.map((row) => {
          const formattedRow = {};
          flatCols.forEach(({ field, headerName }) => {
            const value = row[field];
            const metricKey = field.includes('__') ? field.split('__')[1] : null;
            const metricDef = TREND_METRIC_OPTIONS.find((m) => m.key === metricKey);
            formattedRow[headerName] = metricDef
              ? formatTrendMetricValue(value, metricDef.type)
              : (value ?? '');
          });
          return formattedRow;
        });

        const totalsExportRow = {};
        flatCols.forEach(({ field, headerName }) => {
          const metricKey = field.includes('__') ? field.split('__')[1] : null;
          const metricDef = TREND_METRIC_OPTIONS.find((m) => m.key === metricKey);
          totalsExportRow[headerName] = metricDef
            ? formatTrendMetricValue(trendTotalsRow[field], metricDef.type)
            : (trendTotalsRow[field] ?? '');
        });
        exportData.push(totalsExportRow);


        const ws = XLSX.utils.json_to_sheet(exportData);
        const colWidths = [];
        Object.keys(exportData[0] || {}).forEach((key, index) => {
          const maxLength = Math.max(
            key.length,
            ...exportData.map((row) => (row[key] ? String(row[key]).length : 0))
          );
          colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
        });
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trend Analysis');

        const currentDate = new Date();
        const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
          .toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
        XLSX.writeFile(wb, `Trend Analysis_${dateStr}.xlsx`);

        setMessage('Export completed successfully!');
        setMessageType('success');
        setShowMenuDropdown(false);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
        setMessage('Error exporting data to Excel');
        setMessageType('error');
        setShowMenuDropdown(false);
      }
      return;
    }

    const data = currentRowData;

    // ✅ NEW: respect the grid's checkbox selection — checked rows win,
    // then whatever's currently filtered, then everything.
    const { rows: exportRows } = getExportRows(data);

    if (!exportRows || exportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      // Get column defs based on report type
      const columnDefs = isClientResults
        ? getClientResultsColumnDefs(mailCodeCellRenderer)
        : isDailyRevenue
          ? getDailyRevenueColumnDefs()          // ✅ NEW
          : getMonthlyMailVolumeColumnDefs(jobIdCellRenderer);

      // ✅ FIX: only export columns currently VISIBLE in the grid (was:
      // always exporting every column in columnDefs regardless of what
      // the user hid/dragged out via the column menu).
      const columnsToExport = getExportColumnsFor(columnDefs);

      // Transform data with formatting
      const exportData = exportRows.map((row) => {
        if (row?.isTotalsRow) return null; // Skip totals row from main data

        const formattedRow = {};

        columnsToExport.forEach(({ field, headerName }) => {
          formattedRow[headerName] = formatVolumeFieldValue(field, row[field]);
        });

        return formattedRow;
      }).filter(row => row !== null); // Remove null entries from totals

      // Add totals row at the end
      // ✅ FIXED: Map field names to totals (now via the shared helper so
      // Excel and Print To Pdf can never drift apart)
      // ✅ FIX (item 3 — Revenue Report only): pass the exact `exportRows`
      // being written to Excel, so the Total row reflects checked/filtered
      // rows instead of the tab-wide totals state.
      const totalsRowExport = {};
      columnsToExport.forEach(({ field, headerName }, idx) => {
        if (idx === 0) {
          totalsRowExport[headerName] = 'TOTAL';
        } else {
          totalsRowExport[headerName] = getVolumeTotalValueForField(field, exportRows);
        }
      });

      exportData.push(totalsRowExport);

      // Create Excel sheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Auto-fit column widths
      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => {
            const value = row[key];
            return value ? String(value).length : 0;
          })
        );
        colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      // ✅ FIX (item 2 — Revenue Report only): sheet/export label now says
      // "Revenue Report" to match the dropdown option's display text,
      // instead of the old internal "Daily Revenue" name.
      const sheetName = isClientResults
        ? 'Client Results'
        : isDailyRevenue
          ? 'Revenue Report'          // ✅ CHANGED
          : 'Monthly Mail Volume';
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename with date
      const currentDate = new Date();
      const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
      const fileName = `${sheetName}_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);
      setMessage('Export completed successfully!');
      setMessageType('success');
      setShowMenuDropdown(false);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setMessage('Error exporting data to Excel');
      setMessageType('error');
      setShowMenuDropdown(false);
    }
  };

  // ✅ NEW: PRINT TO PDF — same visual style (header card, blue theme,
  // autoTable) as ListAnalysisTab's Print To Pdf, applied to all four
  // Volume Tracking report types. Uses the exact same data source and
  // column selection as Export to Excel above, so the two exports always
  // agree with each other.
  const printToPDF = () => {
    if (isTrendAnalysis) {
      const { rows: trendExportRows, scope: trendScope } = getExportRows(trendRowData);
      if (!trendExportRows || trendExportRows.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowMenuDropdown(false);
        return;
      }
      try {
        // ✅ Same collision-free column builder used by Export to Excel
        const flatCols = buildTrendExportColumns();

        const body = trendExportRows.map((row) =>
          flatCols.map(({ field }) => {
            const metricKey = field.includes('__') ? field.split('__')[1] : null;
            const metricDef = TREND_METRIC_OPTIONS.find((m) => m.key === metricKey);
            const value = row[field];
            return metricDef ? formatTrendMetricValue(value, metricDef.type) : (value ?? '');
          })
        );
        const totalsRowValues = flatCols.map(({ field }, idx) => {
          // if (idx === 0) return 'TOTAL';
          const metricKey = field.includes('__') ? field.split('__')[1] : null;
          const metricDef = TREND_METRIC_OPTIONS.find((m) => m.key === metricKey);
          return metricDef ? formatTrendMetricValue(trendTotalsRow[field], metricDef.type) : (trendTotalsRow[field] ?? '');
        });
        body.push(totalsRowValues);

        drawVolumePdf({
          head: [flatCols.map((c) => c.headerName)],
          body,
          rightAlignCols: flatCols.map((_, idx) => idx > 1), // List/List Select stay left-aligned
          sheetName: 'Trend Analysis',
          titleText: 'Trend Analysis Report',
          subtitleText: 'TREND ANALYSIS',
          recordCount: trendExportRows.length,
          scope: trendScope,
        });

        setMessage('Export completed successfully!');
        setMessageType('success');
      } catch (error) {
        console.error('Error exporting to PDF:', error);
        setMessage('Error exporting data to PDF');
        setMessageType('error');
      }
      setShowMenuDropdown(false);
      return;
    }

    const data = currentRowData;
    const { rows: pdfExportRows, scope: pdfScope } = getExportRows(data);
    if (!pdfExportRows || pdfExportRows.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowMenuDropdown(false);
      return;
    }

    try {
      const columnDefs = isClientResults
        ? getClientResultsColumnDefs(mailCodeCellRenderer)
        : isDailyRevenue
          ? getDailyRevenueColumnDefs()
          : getMonthlyMailVolumeColumnDefs(jobIdCellRenderer);

      // ✅ Same visible-columns helper used by Export to Excel
      const columnsToExport = getExportColumnsFor(columnDefs);
      const validData = pdfExportRows;

      const body = validData.map((row) =>
        columnsToExport.map(({ field }) => formatVolumeFieldValue(field, row[field]))
      );
      // ✅ FIX (item 3 — Revenue Report only): pass the exact `validData`
      // rows being printed, so the Total row reflects checked/filtered
      // rows instead of the tab-wide totals state.
      const totalsRowValues = columnsToExport.map(({ field }, idx) =>
        idx === 0 ? 'TOTAL' : getVolumeTotalValueForField(field, validData)
      );
      body.push(totalsRowValues);

      // ✅ FIX (item 2 — Revenue Report only): matches the Excel sheet name change
      const sheetName = isClientResults
        ? 'Client Results'
        : isDailyRevenue
          ? 'Revenue Report'          // ✅ CHANGED
          : 'Monthly Mail Volume';

      drawVolumePdf({
        head: [columnsToExport.map((c) => c.headerName)],
        body,
        rightAlignCols: columnsToExport.map(({ field }) => isRightAlignVolumeField(field)),
        sheetName,
        // ✅ FIX (item 2): avoid "Revenue Report Report" — Revenue Report's
        // sheetName is already the full title, others still get " Report" appended
        titleText: isDailyRevenue ? 'Revenue Report' : `${sheetName} Report`,
        subtitleText: sheetName.toUpperCase(),
        recordCount: validData.length,
        scope: pdfScope,
        // ✅ FIX (item 5 — Revenue Report only): right-align numeric column
        // HEADERS to match their body values — see drawVolumePdf below.
        alignHeaderWithBody: isDailyRevenue,
      });

      setMessage('Export completed successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setMessage('Error exporting data to PDF');
      setMessageType('error');
    }
    setShowMenuDropdown(false);
  };

  // ✅ NEW: shared PDF page renderer (styled header card + autoTable) — the
  // same look ListAnalysisTab uses for Print To Pdf, factored out here so
  // both the four Volume Tracking report types can reuse one drawing routine.
  //
  // ✅ FIX (item 5 — Revenue Report only): added `alignHeaderWithBody` flag.
  // Previously only body cells were right-aligned for numeric columns while
  // their headers stayed left-aligned, so header text and numeric values
  // didn't line up — most visible on Revenue Report's 13 narrow columns.
  // The flag is opt-in (defaults to false) so Client Results / Monthly
  // Mail Volume / Trend Analysis PDFs render exactly as before.
  const drawVolumePdf = ({ head, body, rightAlignCols, sheetName, titleText, subtitleText, recordCount, scope, alignHeaderWithBody = false }) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 20;
    const cardX = marginX;
    const cardY = 20;
    const cardWidth = pageWidth - marginX * 2;

    const currentDate = new Date();
    const reportDateStr = `${(currentDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

    const headerBarHeight = 60;
    const detailsBarHeight = 22;
    const cardHeight = headerBarHeight + detailsBarHeight;

    const BLUE_DARK = [30, 75, 120];
    const BLUE_DEEP = [22, 58, 95];

    // ── 1) Header bar ──────────────────────────────────────────
    doc.setFillColor(...BLUE_DARK);
    doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

    const logoW = 90;
    doc.setFillColor(...BLUE_DEEP);
    doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(180, 200, 220);
    doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

    const titleAreaX = cardX + logoW;
    const titleAreaW = cardWidth - logoW;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + headerBarHeight / 2 + 2, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(200, 215, 230);
    doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + headerBarHeight / 2 + 16, { align: 'center' });

    const metaX = cardX + cardWidth - 14;
    const metaCenterY = cardY + headerBarHeight / 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text('Report', metaX, metaCenterY - 2, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(180, 200, 220);
    doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });

    // ── 2) Details strip ──────────────────────────────────────
    const detailsY = cardY + headerBarHeight;
    doc.setFillColor(...BLUE_DEEP);
    doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(`${sheetName.toUpperCase()} DETAILS`, cardX + 14, detailsY + detailsBarHeight / 2 + 3);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(200, 215, 230);
    const scopeLabel = scope === 'selected' ? 'Selected' : scope === 'filtered' ? 'Filtered' : 'All';
    doc.text(
      `${scopeLabel} Records: ${recordCount}  ·  Confidential`,
      cardX + cardWidth - 12,
      detailsY + detailsBarHeight / 2 + 3,
      { align: 'right' }
    );

    // ── Table ────────────────────────────────────────────────
    const tableStartY = cardY + cardHeight + 10;

    autoTable(doc, {
      head,
      body,
      startY: tableStartY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 5, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [46, 111, 159], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5 },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      didParseCell: (data) => {
        if (!rightAlignCols?.[data.column.index]) return;
        if (data.section === 'body') {
          data.cell.styles.halign = 'right';
        } else if (alignHeaderWithBody && data.section === 'head') {
          // ✅ FIX (item 5 — Revenue Report only): keep numeric column
          // headers right-aligned so they sit directly above their values
          // instead of the header text drifting to the left of the column.
          data.cell.styles.halign = 'right';
        }
      },
      didDrawPage: () => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`Page ${pageCurrent} of ${pageCount}`, pageWidth - marginX, pageHeight - 10, { align: 'right' });
      },
    });

    const date = new Date();
    doc.save(`${sheetName.replace(/\s+/g, '-')}-${date.toISOString().split('T')[0]}.pdf`);
  };

  // ✅ CALLBACK TO CLOSE MESSAGE
  const handleMessageClose = () => {
    setMessage('');
    setMessageType('success');
  };

  // ✅ CLOSE MENU ON OUTSIDE CLICK - ADDED
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setShowMenuDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {

    if (restoringRef.current) { restoringRef.current = false; return; }


    // ✅ CLEAR CLIENT STATE
    setSelectedClients([]);
    setSelectedClientIds([]);
    setClientSearchTerm("");

    // ✅ CLEAR JOB STATE
    setJobSearch("");
    setShowJobDropdown(false);

    // ✅ NEW: CLEAR DAILY REVENUE JOB STATE
    setRevenueJobSearch("");
    setShowRevenueJobDropdown(false);

    // ✅ NEW: CLEAR TREND ANALYSIS JOB/COLUMNS STATE
    setTrendJobSearch("");
    setShowTrendJobDropdown(false);
    setShowColumnsPopup(false);
    setSelectedTrendColumns({
      totalMailed: true,
      nonDonors: true,
      donations: true,
      responseRate: true,
      income: true,
      avgGift: true,
      cost: true,
      net: true,
      inOut: false,
    });

    // ✅ RESET FILTERS
    setClientResultFilters({
      fromDate: "",
      toDate: "",
      clientIds: [],
    });

    setMonthlyMailVolumeFilters({
      fromDate: "",
      toDate: "",
      clientIds: [],
      jobIDs: [],
    });

    // ✅ NEW: RESET DAILY REVENUE FILTERS
    setDailyRevenueFilters({
      fromDate: "",
      toDate: "",
      clientIds: [],
      jobIDs: [],
      period: "Daily",
    });

    // ✅ NEW: RESET TREND ANALYSIS FILTERS
    setTrendFilters({
      fromDate: "",
      toDate: "",
      clientIds: [],
      jobIds: [],
      list: "",
      listSel: "",
    });

    // ✅ CLEAR GRID DATA 🔥
    setFilteredData([]);  // ✅ local filtered state clear

    // ✅ RESET TOTALS
    setTotals({
      totalDonors: 0,
      totalNonDonors: 0,
      totalPieces: 0,
      totalAmount: 0,
    });

    // ✅ FORCE CLEAR GRID DATA FROM PARENT (IMPORTANT)
    handleClearClientResults?.();        // ✅ clear API data
    handleClearMonthlyMailVolume?.();    // ✅ clear API data
    setDailyRevenueRows([]);              // ✅ NEW: clear locally-fetched Daily Revenue rows
    handleClearDailyRevenue?.();         // ✅ NEW: clear Daily Revenue API data
    setTrendRows([]);                     // ✅ NEW: clear locally-fetched Trend Analysis rows
    setTrendTotals({});
    setListSegmentOptions([]);
    setSelectedListMasterId(null);
    handleClearTrendAnalysis?.();        // ✅ NEW: clear Trend Analysis API data

    // ✅ RESET DROPDOWN
    setShowClientDropdown(false);

    // ✅ CLEAR JOB API DATA
    fetchJobsByClient?.([]);

  }, [volumeTrackingType]);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target)) {
        setShowJobDropdown(false);
      }
      // ✅ NEW: close Daily Revenue job dropdown on outside click
      if (revenueJobDropdownRef.current && !revenueJobDropdownRef.current.contains(e.target)) {
        setShowRevenueJobDropdown(false);
      }
      // ✅ NEW: close Trend Analysis job dropdown & Choose Columns popup on outside click
      if (trendJobDropdownRef.current && !trendJobDropdownRef.current.contains(e.target)) {
        setShowTrendJobDropdown(false);
      }
      if (columnsPopupRef.current && !columnsPopupRef.current.contains(e.target)) {
        setShowColumnsPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFilterChanged = (params) => {
    const filteredRows = [];
    params.api.forEachNodeAfterFilter((node) => {
      if (node.data) filteredRows.push(node.data);
    });
    setFilteredData(filteredRows);
    setTotals(calculateTotals(filteredRows));
  };

  useEffect(() => {
    const sourceData = filteredData.length > 0 ? filteredData : currentRowData || [];
    setTotals(calculateTotals(sourceData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientResultsData, monthlyMailVolumeDataState, dailyRevenueRows, filteredData, volumeTrackingType]);

  const totalsRow = isClientResults
    ? {
      isTotalsRow: true,
      clientId: "",
      clientName: "TOTAL",
      date: "",
      dayOfWeek: "",
      donors: totals.totalDonors,
      nonDonors: totals.totalNonDonors,
      pieces: totals.totalPieces,
      amount: totals.totalAmount,
    }
    : isDailyRevenue
      ? {
        // ✅ NEW: totals row for Daily Revenue (on-screen pinned row —
        // Amount Cumulative / Total Income intentionally omitted here too,
        // their column cellRenderers now render blank on this row anyway)
        isTotalsRow: true,
        clientID: "",
        clientName: "TOTAL",
        period: "",
        date: "",
        nonDonorsDay: totals.totalNonDonors,
        donorsDay: totals.totalDonors,
        amountDay: totals.totalAmount,
      }
      : {
        isTotalsRow: true,
        clientID: "",
        clientName: "TOTAL",
        quantityMailed: totals.totalPieces,
        totalIncome: totals.totalAmount,
        totalMailings: totals.totalMailings,
      };

  const handleClientChange = (client) => {
    const id = client.clientID;

    setSelectedClients((prev) => {
      let updated;

      if (prev.some((c) => c.clientID === id)) {
        updated = prev.filter((c) => c.clientID !== id);
      } else {
        updated = [...prev, client];
      }

      const ids = updated.map((c) => c.clientID);

      setSelectedClientIds(ids);

      // ✅ FETCH NEW JOBS
      fetchJobsByClient?.(ids);

      // ✅ CRITICAL FIX — reset job selection
      setMonthlyMailVolumeFilters((p) => ({
        ...p,
        jobIDs: [],
      }));
      // ✅ NEW — reset Daily Revenue job selection too
      setDailyRevenueFilters((p) => ({
        ...p,
        jobIDs: [],
      }));
      // ✅ NEW — reset Trend Analysis job comparison too
      setTrendFilters((p) => ({
        ...p,
        jobIds: [],
      }));

      return updated;
    });

    setClientSearchTerm("");
    setShowClientDropdown(false);
  };


  const handleSelectAll = () => {
    setSelectedClients(clients);

    const ids = clients.map((c) => c.clientID);
    setSelectedClientIds(ids);

    fetchJobsByClient?.(ids);

    // ✅ RESET jobs
    setMonthlyMailVolumeFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: RESET Daily Revenue jobs
    setDailyRevenueFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: RESET Trend Analysis job comparison
    setTrendFilters((p) => ({
      ...p,
      jobIds: [],
    }));

    setClientSearchTerm("");
  };

  const handleClearAll = () => {
    setSelectedClients([]);
    setSelectedClientIds([]);

    fetchJobsByClient?.([]);

    // ✅ CLEAR jobs
    setMonthlyMailVolumeFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: CLEAR Daily Revenue jobs
    setDailyRevenueFilters((p) => ({
      ...p,
      jobIDs: [],
    }));
    // ✅ NEW: CLEAR Trend Analysis job comparison
    setTrendFilters((p) => ({
      ...p,
      jobIds: [],
    }));

    setClientSearchTerm("");
  };

  useEffect(() => {
    if (isClientResults) {
      setClientResultFilters((prev) => ({ ...prev, clientIds: selectedClientIds }));
    } else if (isDailyRevenue) {
      // ✅ NEW: keep Daily Revenue's own filter object in sync
      setDailyRevenueFilters((prev) => ({ ...prev, clientIds: selectedClientIds }));
      fetchJobsByClient?.(selectedClientIds);
    } else if (isTrendAnalysis) {
      // ✅ NEW: keep Trend Analysis's own filter object in sync
      setTrendFilters((prev) => ({ ...prev, clientIds: selectedClientIds }));
      fetchJobsByClient?.(selectedClientIds);
    } else {
      setMonthlyMailVolumeFilters((prev) => ({ ...prev, clientIds: selectedClientIds }));
      // Fetch jobs for the chosen clients (mirrors Job Reports behaviour)
      fetchJobsByClient?.(selectedClientIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientIds, isClientResults, isDailyRevenue, isTrendAnalysis]);

  // Clear — Client Results
  const handleClearClientResultsFilters = () => {
    handleClearAll();
    setClientResultFilters({ fromDate: "", toDate: "", clientIds: [] });
    handleClearClientResults();
  };

  // Clear — Monthly Mail Volume (also clears job selection + search)
  const handleClearMonthlyMailVolumeFilters = () => {
    handleClearAll();
    setJobSearch("");
    setMonthlyMailVolumeFilters({
      clientIds: [],
      jobIDs: [],
      fromDate: "",
      toDate: "",
    });
    fetchJobsByClient?.([]);
    handleClearMonthlyMailVolume();
  };

  // ✅ NEW: real API call — GetRevenueReportData — fired by Daily Revenue's "Run Report"
  const handleFetchDailyRevenue = async () => {
    setDailyRevenueLoading(true);
    try {
      const params = {
        clientIDs: (dailyRevenueFilters.clientIds || []).join(','),
        jobIDs: (dailyRevenueFilters.jobIDs || []).join(','),
        startDate: dailyRevenueFilters.fromDate || undefined,
        endDate: dailyRevenueFilters.toDate || undefined,
        periodType: dailyRevenueFilters.period || 'Daily',
      };

      const data = await GetRevenueReportData(params);
      setDailyRevenueRows(Array.isArray(data) ? data : []);

      // Let the parent know too, in case it wants to react (optional hook)
      fetchDailyRevenue?.(dailyRevenueFilters);
    } catch (error) {
      console.error('Error fetching Daily Revenue report:', error);
      setMessage('Failed to load Daily Revenue report');
      setMessageType('error');
      setDailyRevenueRows([]);
    } finally {
      setDailyRevenueLoading(false);
    }
  };

  // ✅ NEW: Clear — Daily Revenue (also clears job selection, search & period)
  const handleClearDailyRevenueFilters = () => {
    handleClearAll();
    setRevenueJobSearch("");
    setDailyRevenueFilters({
      clientIds: [],
      jobIDs: [],
      fromDate: "",
      toDate: "",
      period: "Daily",
    });
    setDailyRevenueRows([]);
    fetchJobsByClient?.([]);
    handleClearDailyRevenue();
  };

  // ✅ NEW: real API call — GetTrendAnalysisReport — fired by Trend Analysis's "Run Report"
  const handleFetchTrendAnalysis = async () => {
    setTrendLoading(true);
    try {
      const params = {
        clientIDs: (trendFilters.clientIds || []).join(','),
        jobIDs: (trendFilters.jobIds || []).join(','),
        startDate: trendFilters.fromDate || undefined,
        endDate: trendFilters.toDate || undefined,
        listName: trendFilters.list || undefined,
        listSegment: trendFilters.listSel || undefined,
      };

      const data = await GetTrendAnalysisReport(params);
      const apiRows = Array.isArray(data) ? data : [];

      // The API returns flat rows keyed by jobCode (e.g. "H-888"), but our
      // comparison columns are keyed by the numeric jobID the user picked —
      // build a jobCode -> jobID lookup from the currently selected jobs.
      const codeToId = {};
      selectedTrendJobIds.forEach((jobId) => {
        const code = jobLabelMap[jobId];
        if (code) codeToId[code] = jobId;
      });

      // Group the flat rows by (listName, listSegment) into the
      // { list, listSel, metrics: { [jobID]: {...} } } shape the grid expects
      const grouped = {};
      apiRows.forEach((row) => {
        const key = `${row.listName ?? ''}|||${row.listSegment ?? ''}`;
        if (!grouped[key]) {
          grouped[key] = { list: row.listName ?? '', listSel: row.listSegment ?? '', metrics: {} };
        }
        const jobId = codeToId[row.jobCode] ?? row.jobCode;
        grouped[key].metrics[jobId] = {
          totalMailed: row.totalMailed,
          nonDonors: row.nonDonors,
          donations: row.donations,
          responseRate: row.responseRate,
          income: row.income,
          avgGift: row.avgGift,
          cost: row.cost,
          net: row.net,
        };
      });
      setTrendRows(Object.values(grouped));

      // Grand Total per job, computed from the raw (ungrouped) API rows
      const totals = {};
      selectedTrendJobIds.forEach((jobId) => {
        const code = jobLabelMap[jobId];
        const rowsForJob = apiRows.filter((r) => r.jobCode === code);
        const sum = (key) => rowsForJob.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
        const totalMailed = sum('totalMailed');
        const donations = sum('donations');
        const income = sum('income');
        totals[jobId] = {
          totalMailed,
          nonDonors: sum('nonDonors'),
          donations,
          income,
          cost: sum('cost'),
          net: sum('net'),
          avgGift: donations ? income / donations : 0,
          responseRate: totalMailed ? (donations / totalMailed) * 100 : 0,
        };
      });
      setTrendTotals(totals);

      // Let the parent know too, in case it wants to react (optional hook)
      fetchTrendAnalysis?.(trendFilters);
    } catch (error) {
      console.error('Error fetching Trend Analysis report:', error);
      setMessage('Failed to load Trend Analysis report');
      setMessageType('error');
      setTrendRows([]);
      setTrendTotals({});
    } finally {
      setTrendLoading(false);
    }
  };

  // ✅ NEW: Clear — Trend Analysis (also clears job comparison, search & columns)
  const handleClearTrendAnalysisFilters = () => {
    handleClearAll();
    setTrendJobSearch("");
    setTrendFilters({
      clientIds: [],
      jobIds: [],
      fromDate: "",
      toDate: "",
      list: "",
      listSel: "",
    });
    setTrendRows([]);
    setTrendTotals({});
    setListSegmentOptions([]);
    setSelectedListMasterId(null);
    fetchJobsByClient?.([]);
    handleClearTrendAnalysis();
  };

  // ✅ NEW: "Choose Columns" popup footer actions
  const handleSelectAllTrendColumns = () => {
    const all = {};
    TREND_METRIC_OPTIONS.forEach((m) => { all[m.key] = true; });
    setSelectedTrendColumns(all);
  };
  const handleClearAllTrendColumns = () => {
    const none = {};
    TREND_METRIC_OPTIONS.forEach((m) => { none[m.key] = false; });
    setSelectedTrendColumns(none);
  };
  const selectedTrendColumnsCount = TREND_METRIC_OPTIONS.filter((m) => selectedTrendColumns[m.key]).length;

  // ✅ FIX: memoize so this only produces a NEW array when the comparison
  // actually changes structurally (jobs picked / columns checked / job
  // labels loaded) — not on every unrelated re-render (typing in a filter,
  // opening a dropdown, etc.). Paired with the refreshHeader() fix in
  // AgGrid.jsx, this is what makes the merged group headers stay correct
  // when a 2nd/3rd comparison job is added.
  const trendColumnDefs = useMemo(
    () => getTrendAnalysisColumnDefs(selectedTrendJobIds, selectedTrendColumns, jobLabelMap),
    [selectedTrendJobIds, selectedTrendColumns, jobLabelMap]
  );

  return (
    <>
      {/* ✅ NEW: scoped styling — polished "Choose Columns" popup + a more
          professional grid theme (header colour, zebra rows, hover, elevation,
          and distinct accent per Job comparison group in Trend Analysis).
          Scoped under .vt-report-tab / .vt-grid-card so nothing outside this
          component is affected. */}
      <style>{`
        .vt-choose-columns-btn {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          width: 100%; min-height: 38px; padding: 0 12px;
          border: 1px solid #d7dee5; color: #2E6F9F; background: #fff;
          border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 600;
          transition: background-color .15s ease, border-color .15s ease, color .15s ease;
        }
        .vt-choose-columns-btn:hover { background: #f0f6fb; border-color: #2E6F9F; }
        .vt-choose-columns-btn.active { background: #2E6F9F; color: #fff; border-color: #2E6F9F; }
        .vt-choose-columns-badge {
          background: #2E6F9F; color: #fff; border-radius: 999px; font-size: 11px;
          font-weight: 700; padding: 1px 7px; line-height: 16px; min-width: 16px;
        }
        .vt-choose-columns-btn.active .vt-choose-columns-badge { background: #fff; color: #2E6F9F; }

        .vt-columns-popup {
          position: absolute; top: calc(100% + 8px); right: 0; width: 270px;
          background: #fff; border: 1px solid #e3e8ee; border-radius: 10px;
          box-shadow: 0 14px 34px rgba(15, 32, 55, 0.18); z-index: 9999; overflow: hidden;
          animation: vtPopIn .14s ease-out;
        }
        @keyframes vtPopIn {
          from { opacity: 0; transform: translateY(-6px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .vt-columns-popup-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          padding: 12px 14px; background: #f7f9fb; border-bottom: 1px solid #e9edf1;
        }
        .vt-columns-popup-title { font-size: 13px; font-weight: 700; color: #1f2d3d; }
        .vt-columns-popup-sub { font-size: 11px; color: #8a97a6; margin-top: 2px; }
        .vt-columns-popup-close {
          border: none; background: transparent; cursor: pointer; color: #8a97a6;
          font-size: 18px; line-height: 1; padding: 2px 4px; border-radius: 4px;
        }
        .vt-columns-popup-close:hover { color: #1f2d3d; background: #e9edf1; }
        .vt-columns-popup-list { max-height: 260px; overflow-y: auto; padding: 6px; }
        .vt-columns-popup-row {
          display: flex; align-items: center; gap: 10px;
          padding: 8px; border-radius: 6px; cursor: pointer;
          font-size: 13px; color: #33404d; transition: background-color .1s ease;
        }
        .vt-columns-popup-row:hover { background: #f0f6fb; }
        .vt-columns-popup-row input[type="checkbox"] {
          width: 15px; height: 15px; accent-color: #2E6F9F; cursor: pointer; flex-shrink: 0;
        }
        .vt-metric-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .vt-columns-popup-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 14px; border-top: 1px solid #e9edf1; background: #fbfcfd;
        }
        .vt-columns-popup-link {
          border: none; background: none; color: #2E6F9F; font-size: 12px;
          font-weight: 600; cursor: pointer; padding: 0;
        }
        .vt-columns-popup-link:hover { text-decoration: underline; }
        .vt-columns-popup-count { font-size: 11px; color: #8a97a6; }
        .vt-columns-popup-done {
          border: none; background: #2E6F9F; color: #fff; font-size: 12px;
          font-weight: 700; padding: 6px 14px; border-radius: 6px; cursor: pointer;
        }
        .vt-columns-popup-done:hover { background: #255a83; }

        /* ---- Grid: a cleaner, more "industrial" report-grid look ---- */
        .vt-grid-card {
          border: 1px solid #e3e8ee; border-radius: 10px; overflow: hidden;
          box-shadow: 0 1px 4px rgba(15, 32, 55, 0.06);
        }
        .vt-grid-card .ag-header,
        .vt-grid-card .ag-header-cell,
        .vt-grid-card .ag-header-group-cell,
        .vt-grid-card .ag-pinned-left-header {
          background-color: #0a558c !important;
          border-color: #0a558c !important;
        }
        .vt-grid-card .ag-header-cell-text,
        .vt-grid-card .ag-header-group-text,
        .vt-grid-card .ag-header-icon {
          color: #ffffff !important;
          font-weight: 600;
        }
        /* Trend Analysis: renders the literal \n in headerName as a real line
           break, so "Job Code" + "Metric Name" stack into a clean 2-line
           header that reads like a merged group label — on flat columns.
           Uses white-space:pre (not pre-line) so each line stays put and
           never wraps again even if a metric label is a bit long, which
           was what caused the earlier overlap. */
        .vt-grid-card .ag-header-cell-text {
          white-space: pre;
          line-height: 1.3;
          font-size: 14px;
        }
        .vt-grid-card .ag-header-cell-label {
          overflow: visible;
        }
        .vt-grid-card .ag-row-odd { background-color: #f7fafc; }
        .vt-grid-card .ag-row-even { background-color: #ffffff; }
        .vt-grid-card .ag-row-hover { background-color: #eaf3fb !important; }
        .vt-grid-card .ag-cell { font-size: 13px; }
        .vt-grid-card .ag-row-pinned { border-top: 2px solid #2E6F9F !important; }
        .vt-grid-card .ag-pinned-left-cols-container .ag-cell { font-weight: 500; }
        /* Distinct accent per compared Job ID (Trend Analysis) — flat columns */
        .vt-grid-card .ag-header-cell.vt-job-group-0 { background-color: #2E6F9F !important; }
        .vt-grid-card .ag-header-cell.vt-job-group-1 { background-color: #3f8f5f !important; }
        .vt-grid-card .ag-header-cell.vt-job-group-2 { background-color: #8a5fb8 !important; }

        /* ---- Trend Analysis: per-job KPI summary cards ---- */
        .vt-kpi-card {
          background: #fff; border: 1px solid #e3e8ee; border-top: 4px solid #2E6F9F;
          border-radius: 10px; padding: 14px 16px; box-shadow: 0 1px 4px rgba(15, 32, 55, 0.06);
        }
        .vt-kpi-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
        .vt-kpi-card-badge {
          display: inline-flex; align-items: center; justify-content: center;
          width: 20px; height: 20px; border-radius: 50%; color: #fff; font-size: 11px; font-weight: 700; flex-shrink: 0;
        }
        .vt-kpi-card-title { font-size: 14px; font-weight: 700; color: #1f2d3d; }
        .vt-kpi-card-body { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
        .vt-kpi-stat { display: flex; flex-direction: column; gap: 2px; }
        .vt-kpi-stat-label { font-size: 11px; color: #8a97a6; }
        .vt-kpi-stat-value { font-size: 15px; font-weight: 700; color: #1f2d3d; }

        /* ---- Trend Analysis: empty state before any jobs are picked ---- */
        .vt-empty-state {
          margin-top: 20px; padding: 48px 24px; text-align: center;
          border: 1px dashed #d7dee5; border-radius: 10px; background: #fbfcfd; color: #33404d;
        }
        .vt-empty-state-title { font-size: 15px; font-weight: 700; margin-top: 12px; color: #1f2d3d; }
        .vt-empty-state-sub {
          font-size: 13px; color: #8a97a6; margin-top: 4px;
          max-width: 420px; margin-left: auto; margin-right: auto;
        }

        /* ---- FIX (item 4 — Revenue Report only): the Period dropdown's
           descender letters (e.g. the "y" in "Quarterly") were getting
           clipped by the select's line-height/padding. Bumping both fixes
           the visible cropping without affecting any other dropdown. ---- */
        .vt-period-select {
          line-height: 1.5 !important;
          padding-top: 6px !important;
          padding-bottom: 8px !important;
        }
      `}</style>

      <SuccessMessage
        message={message}
        onClose={handleMessageClose}
        type={messageType}
      />

      {/* Report type dropdown */}
      <div style={{ marginTop: "20px", width: "250px" }}>
        <label style={{ fontWeight: "600", display: "block", marginBottom: "8px" }}>
          Select Report Type
        </label>
        <select
          style={styles.input}
          value={volumeTrackingType}
          onChange={(e) => setVolumeTrackingType(e.target.value)}
        >
          <option>Client Results</option>
          <option>Monthly Mail Volume</option>
          {/* ✅ Display text only — value stays "Daily Revenue" so every
              isDailyRevenue / sheetName / navigation check elsewhere in
              this file keeps working unchanged. */}
         {!isClientOrConsultant && (
            <>
              {/* ✅ Display text only — value stays "Daily Revenue" so every
                  isDailyRevenue / sheetName / navigation check elsewhere in
                  this file keeps working unchanged. */}
              <option value="Daily Revenue">Revenue Report</option>
              <option>Trend Analysis</option>
            </>
          )}
        </select>
      </div>

      {/* CLIENT RESULTS Filters */}
      {isClientResults && (
        <div className="report-filter-container">
          <div className="filter-header" style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '0px 10px 0px 0px', alignItems: 'center',
            backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
          }}>
            <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
            <button
              onClick={toggleFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: '#fff', padding: '2px 6px',
                transition: 'transform 0.2s ease',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <img src="../assets/upward-arrow-icon.png"
                alt={showFilters ? 'Hide filters' : 'Show filters'}
                style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
            </button>
          </div>

          {showFilters && (
            <>

              <div className="report-filter-body">
                <div className="report-filter-row1">
                  <div className="report-filter-field" style={{ position: 'relative', maxWidth: '350px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Client(s)
                    </label>

                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                      {/* INPUT BOX */}
                      <div
                        onClick={() => setShowClientDropdown(true)}
                        style={{
                          minHeight: "38px",
                          height: "auto",
                          boxSizing: "border-box",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          paddingLeft: "8px",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          cursor: "text",
                          backgroundColor: "#fff",
                          alignItems: "center",
                          overflowY: "auto"
                        }}
                      >
                        {/* CHIPS */}
                        {selectedClients.length > 1 ? (
                          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                            {selectedClients.length} clients selected
                            <span
                              onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                              style={{ marginLeft: 6, cursor: "pointer" }}
                            >×</span>
                          </span>
                        ) : (
                          selectedClients.map((c) => (
                            <span
                              key={c.clientID}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                background: "#e9ecef",
                                padding: "2px 6px",
                                borderRadius: "3px",
                                fontSize: "13px",
                                whiteSpace: "nowrap"
                              }}
                            >
                              {c.clientName}
                              <span
                                onClick={(e) => { e.stopPropagation(); handleClientChange(c); }}
                                style={{ marginLeft: 6, cursor: "pointer" }}
                              >×</span>
                            </span>
                          ))
                        )}

                        {/* SEARCH */}
                        <input
                          type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
                          style={{
                            flex: 1,
                            minWidth: selectedClients.length === 0 ? "120px" : "40px",
                            width: "40px",
                            border: "none",
                            outline: "none",
                            fontSize: "14px",
                            padding: 0
                          }}
                        />
                      </div>

                      {/* DROPDOWN */}
                      {showClientDropdown && (
                        <div
                          style={{
                            position: "absolute", top: "100%", left: 0, width: "100%",
                            backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px",
                            marginTop: "4px", zIndex: 9999, maxHeight: "250px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleSelectAll}>Select All</button>
                            <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleClearAll}>Clear All</button>
                          </div>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChange(client)}
                                  style={{
                                    padding: "8px", display: "flex", alignItems: "center", cursor: "pointer",
                                    backgroundColor: selectedClients.some(c => c.clientID === client.clientID) ? "#e6f7ff" : "#fff"
                                  }}
                                >
                                  <input type="checkbox" checked={selectedClients.some(c => c.clientID === client.clientID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{client.clientName}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No clients found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mail Date — Job Reports style */}
                  <div className="mail-date-filter">
                    <label className="mail-date-filter-label">Date Range</label>
                    <div className="">
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!clientResultFilters.fromDate ? 'empty' : ''}`}
                          value={clientResultFilters.fromDate}
                          onChange={(e) => {
                            const newFromDate = e.target.value;
                            if (clientResultFilters.toDate && newFromDate > clientResultFilters.toDate) return;
                            setClientResultFilters((prev) => ({ ...prev, fromDate: newFromDate }));
                          }}
                          max={clientResultFilters.toDate || undefined}
                          title="Start Date"
                        />
                      </div>
                      <span className="arrow-icon">→</span>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!clientResultFilters.toDate ? 'empty' : ''}`}
                          value={clientResultFilters.toDate}
                          onChange={(e) => {
                            const newToDate = e.target.value;
                            if (clientResultFilters.fromDate && newToDate < clientResultFilters.fromDate) return;
                            setClientResultFilters((prev) => ({ ...prev, toDate: newToDate }));
                          }}
                          min={clientResultFilters.fromDate || undefined}
                          title="End Date"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="report-filter-buttons" style={{ justifyContent: "flex-end" }}>
                  {(() => {
                    const hasFilters =
                      selectedClients.length > 0 ||
                      !!clientResultFilters.fromDate ||
                      !!clientResultFilters.toDate;
                    return (
                      <>
                        <button className="report-btn-run" onClick={fetchClientResults} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Run Report</button>
                        <button className="report-btn-clear" onClick={handleClearClientResultsFilters} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Clear</button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* MONTHLY MAIL VOLUME Filters */}
      {!isClientResults && !isDailyRevenue && !isTrendAnalysis && (
        <div className="report-filter-container">
          <div className="filter-header" style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '0px 10px 0px 0px', alignItems: 'center',
            backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
          }}>
            <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
            <button
              onClick={toggleFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: '#fff', padding: '2px 6px',
                transition: 'transform 0.2s ease',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <img src="../assets/upward-arrow-icon.png"
                alt={showFilters ? 'Hide filters' : 'Show filters'}
                style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
            </button>
          </div>


          {showFilters && (
            <>

              <div className="report-filter-body">
                <div className="report-filter-row">
                  {/* Client(s) */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Client(s)
                    </label>

                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                      <div
                        onClick={() => setShowClientDropdown(true)}
                        style={{
                          minHeight: "38px", height: "auto", boxSizing: "border-box",
                          border: "1px solid #ccc", borderRadius: "4px", paddingLeft: "8px",
                          display: "flex", flexWrap: "wrap", gap: "4px", cursor: "text",
                          backgroundColor: "#fff", alignItems: "center", overflowY: "auto"
                        }}
                      >
                        {selectedClients.length > 1 ? (
                          <span style={{ backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}>
                            {selectedClients.length} clients selected
                            <span onClick={(e) => { e.stopPropagation(); handleClearAll(); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                          </span>
                        ) : (
                          selectedClients.map((client) => (
                            <span
                              key={client.clientID}
                              style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}
                            >
                              {client.clientName}
                              <span onClick={(e) => { e.stopPropagation(); handleClientChange(client); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                            </span>
                          ))
                        )}

                        <input
                          type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
                          style={{ flex: 1, minWidth: selectedClients.length === 0 ? "120px" : "40px", border: "none", outline: "none", fontSize: "14px" }}
                        />
                      </div>

                      {showClientDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleSelectAll}>Select All</button>
                            <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleClearAll}>Clear All</button>
                          </div>
                          <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                            {filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChange(client)}
                                  style={{ padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", backgroundColor: selectedClients.some((c) => c.clientID === client.clientID) ? "#e6f7ff" : "#fff" }}
                                >
                                  <input type="checkbox" checked={selectedClients.some((c) => c.clientID === client.clientID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{client.clientName}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No clients found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job ID — searchable chip multiselect (Job Reports style) */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Job ID
                    </label>

                    <div ref={jobDropdownRef} style={{ position: "relative", width: "100%" }}>
                      {/* INPUT BOX */}
                      <div
                        onClick={() => setShowJobDropdown(true)}
                        style={{
                          minHeight: "38px", maxHeight: "100px", border: "1px solid #ccc",
                          borderRadius: "6px", padding: "0px 8px", display: "flex", flexWrap: "wrap",
                          gap: "4px", backgroundColor: "#fff", alignItems: "center",
                          alignContent: "flex-start", cursor: "text", overflowY: "auto"
                        }}
                      >
                        {/* CHIPS */}
                        {selectedJobIDs.length > 1 ? (
                          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                            {selectedJobIDs.length} jobs selected
                            <span
                              onClick={(e) => { e.stopPropagation(); setMonthlyMailVolumeFilters((p) => ({ ...p, jobIDs: [] })); }}
                              style={{ marginLeft: 6, cursor: "pointer" }}
                            >×</span>
                          </span>
                        ) : (
                          (selectedClientIds.length ? jobDetailsJobId : [])
                            .filter((j) => selectedJobIDs.includes(j.jobID))
                            .map((j) => (
                              <span
                                key={j.jobID}
                                style={{ display: "inline-flex", alignItems: "center", background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}
                              >
                                {j.jobCode}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMonthlyMailVolumeFilters((p) => ({
                                      ...p,
                                      jobIDs: (p.jobIDs || []).filter((id) => id !== j.jobID)
                                    }));
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >×</span>
                              </span>
                            ))
                        )}

                        {/* SEARCH */}
                        <input
                          type="text"
                          placeholder={selectedJobIDs.length === 0 ? "Select jobs..." : ""}
                          value={jobSearch}
                          onChange={(e) => { setJobSearch(e.target.value); setShowJobDropdown(true); }}
                          style={{
                            flex: 1,
                            minWidth: selectedJobIDs.length === 0 ? "120px" : "40px",
                            width: "40px", border: "none", outline: "none", fontSize: "14px", padding: 0
                          }}
                        />
                      </div>

                      {/* DROPDOWN */}
                      {showJobDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button
                              style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => {
                                const allIds = filteredJobs.map((j) => j.jobID);
                                const merged = [...new Set([...selectedJobIDs, ...allIds])];
                                setMonthlyMailVolumeFilters((p) => ({ ...p, jobIDs: merged }));
                              }}
                            >Select All</button>
                            <button
                              style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => setMonthlyMailVolumeFilters((p) => ({ ...p, jobIDs: [] }))}
                            >Clear All</button>
                          </div>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredJobs.length > 0 ? (
                              filteredJobs.map((job) => (
                                <div
                                  key={job.jobID}
                                  onClick={() => {
                                    const updated = selectedJobIDs.includes(job.jobID)
                                      ? selectedJobIDs.filter((id) => id !== job.jobID)
                                      : [...selectedJobIDs, job.jobID];
                                    setMonthlyMailVolumeFilters((p) => ({ ...p, jobIDs: updated }));
                                    setJobSearch("");
                                  }}
                                  style={{ padding: "8px", display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: selectedJobIDs.includes(job.jobID) ? "#e6f7ff" : "#fff" }}
                                >
                                  <input type="checkbox" checked={selectedJobIDs.includes(job.jobID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mail Date — Job Reports style */}
                  <div className="mail-date-filter">
                    <label className="mail-date-filter-label">Date Range</label>
                    <div className="">
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!monthlyMailVolumeFilters.fromDate ? 'empty' : ''}`}
                          value={monthlyMailVolumeFilters.fromDate}
                          onChange={(e) => {
                            const newFromDate = e.target.value;
                            if (monthlyMailVolumeFilters.toDate && newFromDate > monthlyMailVolumeFilters.toDate) return;
                            setMonthlyMailVolumeFilters((prev) => ({ ...prev, fromDate: newFromDate }));
                          }}
                          max={monthlyMailVolumeFilters.toDate || undefined}
                          title="Start Date"
                        />
                      </div>
                      <span className="arrow-icon">→</span>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!monthlyMailVolumeFilters.toDate ? 'empty' : ''}`}
                          value={monthlyMailVolumeFilters.toDate}
                          onChange={(e) => {
                            const newToDate = e.target.value;
                            if (monthlyMailVolumeFilters.fromDate && newToDate < monthlyMailVolumeFilters.fromDate) return;
                            setMonthlyMailVolumeFilters((prev) => ({ ...prev, toDate: newToDate }));
                          }}
                          min={monthlyMailVolumeFilters.fromDate || undefined}
                          title="End Date"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="report-filter-buttons">
                  {(() => {
                    const hasFilters =
                      selectedClients.length > 0 ||
                      selectedJobIDs.length > 0 ||
                      !!monthlyMailVolumeFilters.fromDate ||
                      !!monthlyMailVolumeFilters.toDate;
                    return (
                      <>
                        <button className="report-btn-run" onClick={fetchMonthlyMailVolume} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Run Report</button>
                        <button className="report-btn-clear" onClick={handleClearMonthlyMailVolumeFilters} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Clear</button>
                      </>
                    );
                  })()}
                </div>
              </div>

            </>
          )}
        </div>
      )}

      {/* ✅ NEW: DAILY REVENUE Filters — Client(s), Job ID, Tallies Date Range, Period */}
      {isDailyRevenue && (
        <div className="report-filter-container">
          <div className="filter-header" style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '0px 10px 0px 0px', alignItems: 'center',
            backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
          }}>
            <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
            <button
              onClick={toggleFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: '#fff', padding: '2px 6px',
                transition: 'transform 0.2s ease',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <img src="../assets/upward-arrow-icon.png"
                alt={showFilters ? 'Hide filters' : 'Show filters'}
                style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
            </button>
          </div>

          {showFilters && (
            <>
              <div className="report-filter-body">
                <div className="report-filter-row">
                  {/* Client(s) */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '300px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Client(s)
                    </label>

                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                      <div
                        onClick={() => setShowClientDropdown(true)}
                        style={{
                          minHeight: "38px", height: "auto", boxSizing: "border-box",
                          border: "1px solid #ccc", borderRadius: "4px", paddingLeft: "8px",
                          display: "flex", flexWrap: "wrap", gap: "4px", cursor: "text",
                          backgroundColor: "#fff", alignItems: "center", overflowY: "auto"
                        }}
                      >
                        {selectedClients.length > 1 ? (
                          <span style={{ backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}>
                            {selectedClients.length} clients selected
                            <span onClick={(e) => { e.stopPropagation(); handleClearAll(); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                          </span>
                        ) : (
                          selectedClients.map((client) => (
                            <span
                              key={client.clientID}
                              style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}
                            >
                              {client.clientName}
                              <span onClick={(e) => { e.stopPropagation(); handleClientChange(client); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                            </span>
                          ))
                        )}

                        <input
                          type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
                          style={{ flex: 1, minWidth: selectedClients.length === 0 ? "120px" : "40px", border: "none", outline: "none", fontSize: "14px" }}
                        />
                      </div>

                      {showClientDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleSelectAll}>Select All</button>
                            <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleClearAll}>Clear All</button>
                          </div>
                          <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                            {filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChange(client)}
                                  style={{ padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", backgroundColor: selectedClients.some((c) => c.clientID === client.clientID) ? "#e6f7ff" : "#fff" }}
                                >
                                  <input type="checkbox" checked={selectedClients.some((c) => c.clientID === client.clientID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{client.clientName}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No clients found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job ID — searchable chip multiselect */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '300px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Job ID
                    </label>

                    <div ref={revenueJobDropdownRef} style={{ position: "relative", width: "100%" }}>
                      {/* INPUT BOX */}
                      <div
                        onClick={() => setShowRevenueJobDropdown(true)}
                        style={{
                          minHeight: "38px", maxHeight: "100px", border: "1px solid #ccc",
                          borderRadius: "6px", padding: "0px 8px", display: "flex", flexWrap: "wrap",
                          gap: "4px", backgroundColor: "#fff", alignItems: "center",
                          alignContent: "flex-start", cursor: "text", overflowY: "auto"
                        }}
                      >
                        {/* CHIPS */}
                        {selectedRevenueJobIDs.length > 1 ? (
                          <span style={{ background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", whiteSpace: "nowrap" }}>
                            {selectedRevenueJobIDs.length} jobs selected
                            <span
                              onClick={(e) => { e.stopPropagation(); setDailyRevenueFilters((p) => ({ ...p, jobIDs: [] })); }}
                              style={{ marginLeft: 6, cursor: "pointer" }}
                            >×</span>
                          </span>
                        ) : (
                          (selectedClientIds.length ? jobDetailsJobId : [])
                            .filter((j) => selectedRevenueJobIDs.includes(j.jobID))
                            .map((j) => (
                              <span
                                key={j.jobID}
                                style={{ display: "inline-flex", alignItems: "center", background: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}
                              >
                                {j.jobCode}
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDailyRevenueFilters((p) => ({
                                      ...p,
                                      jobIDs: (p.jobIDs || []).filter((id) => id !== j.jobID)
                                    }));
                                  }}
                                  style={{ marginLeft: 6, cursor: "pointer" }}
                                >×</span>
                              </span>
                            ))
                        )}

                        {/* SEARCH */}
                        <input
                          type="text"
                          placeholder={selectedRevenueJobIDs.length === 0 ? "Select jobs..." : ""}
                          value={revenueJobSearch}
                          onChange={(e) => { setRevenueJobSearch(e.target.value); setShowRevenueJobDropdown(true); }}
                          style={{
                            flex: 1,
                            minWidth: selectedRevenueJobIDs.length === 0 ? "120px" : "40px",
                            width: "40px", border: "none", outline: "none", fontSize: "14px", padding: 0
                          }}
                        />
                      </div>

                      {/* DROPDOWN */}
                      {showRevenueJobDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button
                              style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => {
                                const allIds = filteredRevenueJobs.map((j) => j.jobID);
                                const merged = [...new Set([...selectedRevenueJobIDs, ...allIds])];
                                setDailyRevenueFilters((p) => ({ ...p, jobIDs: merged }));
                              }}
                            >Select All</button>
                            <button
                              style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => setDailyRevenueFilters((p) => ({ ...p, jobIDs: [] }))}
                            >Clear All</button>
                          </div>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredRevenueJobs.length > 0 ? (
                              filteredRevenueJobs.map((job) => (
                                <div
                                  key={job.jobID}
                                  onClick={() => {
                                    const updated = selectedRevenueJobIDs.includes(job.jobID)
                                      ? selectedRevenueJobIDs.filter((id) => id !== job.jobID)
                                      : [...selectedRevenueJobIDs, job.jobID];
                                    setDailyRevenueFilters((p) => ({ ...p, jobIDs: updated }));
                                    setRevenueJobSearch("");
                                  }}
                                  style={{ padding: "8px", display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: selectedRevenueJobIDs.includes(job.jobID) ? "#e6f7ff" : "#fff" }}
                                >
                                  <input type="checkbox" checked={selectedRevenueJobIDs.includes(job.jobID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tallies Date Range */}
                  <div className="mail-date-filter">
                    <label className="mail-date-filter-label">Tallies Date Range</label>
                    <div className="">
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!dailyRevenueFilters.fromDate ? 'empty' : ''}`}
                          value={dailyRevenueFilters.fromDate}
                          onChange={(e) => {
                            const newFromDate = e.target.value;
                            if (dailyRevenueFilters.toDate && newFromDate > dailyRevenueFilters.toDate) return;
                            setDailyRevenueFilters((prev) => ({ ...prev, fromDate: newFromDate }));
                          }}
                          max={dailyRevenueFilters.toDate || undefined}
                          title="Start Date"
                        />
                      </div>
                      <span className="arrow-icon">→</span>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!dailyRevenueFilters.toDate ? 'empty' : ''}`}
                          value={dailyRevenueFilters.toDate}
                          onChange={(e) => {
                            const newToDate = e.target.value;
                            if (dailyRevenueFilters.fromDate && newToDate < dailyRevenueFilters.fromDate) return;
                            setDailyRevenueFilters((prev) => ({ ...prev, toDate: newToDate }));
                          }}
                          min={dailyRevenueFilters.fromDate || undefined}
                          title="End Date"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Period — Daily / Weekly / Quarterly */}
                  <div className="report-filter-field" style={{ width: '180px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '0px' }}>
                      Period
                    </label>
                    {/* ✅ FIX (item 4 — Revenue Report only): added
                        "vt-period-select" class so the CSS rule above stops
                        the descender of "y" in "Quarterly" from being clipped. */}
                    <select
                      className="vt-period-select"
                      style={styles.input}
                      value={dailyRevenueFilters.period || 'Daily'}
                      onChange={(e) => setDailyRevenueFilters((prev) => ({ ...prev, period: e.target.value }))}
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Quarterly">Quarterly</option>
                    </select>
                  </div>
                </div>

                <div className="report-filter-buttons">
                  {(() => {
                    const hasFilters =
                      selectedClients.length > 0 ||
                      selectedRevenueJobIDs.length > 0 ||
                      !!dailyRevenueFilters.fromDate ||
                      !!dailyRevenueFilters.toDate;
                    return (
                      <>
                        <button
                          className="report-btn-run"
                          onClick={handleFetchDailyRevenue}
                          disabled={!hasFilters || dailyRevenueLoading}
                          style={(!hasFilters || dailyRevenueLoading) ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                        >
                          {dailyRevenueLoading ? "Loading..." : "Run Report"}
                        </button>
                        <button className="report-btn-clear" onClick={handleClearDailyRevenueFilters} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Clear</button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ✅ NEW: TREND ANALYSIS Filters — Client(s), Job ID (max 3), Date Range, List, Select + Choose Columns */}
      {isTrendAnalysis && (
        <div className="report-filter-container">
          <div className="filter-header" style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '0px 10px 0px 0px', alignItems: 'center',
            backgroundColor: '#2E6F9F', borderRadius: '8px 8px 0px 0px'
          }}>
            <h2 className='filter-heading' style={{ margin: 0, color: '#fff' }}>Filters</h2>
            <button
              onClick={toggleFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', color: '#fff', padding: '2px 6px',
                transition: 'transform 0.2s ease',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <img src="../assets/upward-arrow-icon.png"
                alt={showFilters ? 'Hide filters' : 'Show filters'}
                style={{ width: '12px', height: '12px', transition: 'transform 0.2s ease' }} />
            </button>
          </div>

          {showFilters && (
            <>
              <div className="report-filter-body">
                <div className="report-filter-row">
                  {/* Client(s) — multi-select */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '260px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Client(s)
                    </label>

                    <div ref={clientDropdownRef} style={{ position: "relative", width: "100%" }}>
                      <div
                        onClick={() => setShowClientDropdown(true)}
                        style={{
                          minHeight: "38px", height: "auto", boxSizing: "border-box",
                          border: "1px solid #ccc", borderRadius: "4px", paddingLeft: "8px",
                          display: "flex", flexWrap: "wrap", gap: "4px", cursor: "text",
                          backgroundColor: "#fff", alignItems: "center", overflowY: "auto"
                        }}
                      >
                        {selectedClients.length > 1 ? (
                          <span style={{ backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}>
                            {selectedClients.length} clients selected
                            <span onClick={(e) => { e.stopPropagation(); handleClearAll(); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                          </span>
                        ) : (
                          selectedClients.map((client) => (
                            <span
                              key={client.clientID}
                              style={{ display: "inline-flex", alignItems: "center", backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "3px", fontSize: "13px", whiteSpace: "nowrap" }}
                            >
                              {client.clientName}
                              <span onClick={(e) => { e.stopPropagation(); handleClientChange(client); }} style={{ marginLeft: 6, cursor: "pointer" }}>×</span>
                            </span>
                          ))
                        )}

                        <input
                          type="text"
                          placeholder={selectedClients.length === 0 ? "Select clients..." : ""}
                          value={clientSearchTerm}
                          onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
                          style={{ flex: 1, minWidth: selectedClients.length === 0 ? "120px" : "40px", border: "none", outline: "none", fontSize: "14px" }}
                        />
                      </div>

                      {showClientDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px", borderBottom: "1px solid #eee" }}>
                            <button style={{ background: 'none', border: '1px solid #2E6F9F', color: '#2E6F9F', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleSelectAll}>Select All</button>
                            <button style={{ background: 'none', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }} onClick={handleClearAll}>Clear All</button>
                          </div>
                          <div style={{ overflowY: "auto", maxHeight: "200px" }}>
                            {filteredClients.length > 0 ? (
                              filteredClients.map((client) => (
                                <div
                                  key={client.clientID}
                                  onClick={() => handleClientChange(client)}
                                  style={{ padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", backgroundColor: selectedClients.some((c) => c.clientID === client.clientID) ? "#e6f7ff" : "#fff" }}
                                >
                                  <input type="checkbox" checked={selectedClients.some((c) => c.clientID === client.clientID)} readOnly style={{ marginRight: "6px" }} />
                                  <span style={{ fontSize: "12px" }}>{client.clientName}</span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No clients found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job ID — multi-select, capped at 3 (used for side-by-side comparison) */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '350px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                      Job ID <span style={{ fontWeight: 400, fontSize: '12px', color: '#888' }}>(max {MAX_TREND_JOBS})</span>
                    </label>

                    <div ref={trendJobDropdownRef} style={{ position: "relative", width: "100%" }}>
                      <div
                        onClick={() => setShowTrendJobDropdown(true)}
                        style={{
                          minHeight: "38px", border: "1px solid #ccc",
                          borderRadius: "6px", padding: "0px 8px", display: "flex", flexWrap: "no-wrap",
                          gap: "4px", backgroundColor: "#fff", alignItems: "center",
                          cursor: "text", overflow: "hidden" ,
                        }}
                      >
                        {selectedTrendJobIds.length > 0 ? (
                          selectedTrendJobIds.map((jobId, idx) => {
                            const color = TREND_JOB_ACCENT_COLORS[idx % TREND_JOB_ACCENT_COLORS.length];
                            const jobCode = jobLabelMap[jobId] || jobId;
                            return (
                              <span
                                key={jobId}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: "6px",
                                  background: `${color}14`, borderLeft: `3px solid ${color}`,
                                  padding: "3px 8px 3px 6px", borderRadius: "3px",
                                  fontSize: "13px", whiteSpace: "nowrap", color: "#1f2d3d"
                                }}
                              >
                                <span style={{
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  width: "15px", height: "15px", borderRadius: "50%",
                                  backgroundColor: color, color: "#fff", fontSize: "10px", fontWeight: 700
                                }}>{idx + 1}</span>
                                {jobCode}
                                <span
                                  onClick={(e) => { e.stopPropagation(); handleTrendJobToggle(jobId); }}
                                  style={{ marginLeft: 2, cursor: "pointer", color: "#8a97a6" }}
                                >×</span>
                              </span>
                            );
                          })
                        ) : null}

                        <input
                          type="text"
                          placeholder={selectedTrendJobIds.length === 0 ? "Select up to 3 jobs..." : ""}
                          value={trendJobSearch}
                          onChange={(e) => { setTrendJobSearch(e.target.value); setShowTrendJobDropdown(true); }}
                          style={{
                            flex: 1,
                            minWidth: selectedTrendJobIds.length === 0 ? "120px" : "40px",
                            width: "40px", border: "none", outline: "none", fontSize: "14px", padding: 0
                          }}
                        />
                      </div>

                      {showTrendJobDropdown && (
                        <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: "4px", marginTop: "4px", zIndex: 9999, maxHeight: "250px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", overflow: "hidden" }}>
                          <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: "11px", color: "#888" }}>
                            You can compare up to {MAX_TREND_JOBS} jobs at a time
                          </div>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                            {filteredTrendJobs.length > 0 ? (
                              filteredTrendJobs.map((job) => {
                                const isChecked = selectedTrendJobIds.includes(job.jobID);
                                const isDisabled = !isChecked && selectedTrendJobIds.length >= MAX_TREND_JOBS;
                                const order = selectedTrendJobIds.indexOf(job.jobID);
                                const color = order >= 0 ? TREND_JOB_ACCENT_COLORS[order % TREND_JOB_ACCENT_COLORS.length] : null;
                                return (
                                  <div
                                    key={job.jobID}
                                    onClick={() => { if (!isDisabled) { handleTrendJobToggle(job.jobID); setTrendJobSearch(""); } }}
                                    style={{
                                      padding: "8px", display: "flex", alignItems: "center", gap: "8px",
                                      cursor: isDisabled ? "not-allowed" : "pointer",
                                      opacity: isDisabled ? 0.5 : 1,
                                      backgroundColor: isChecked ? "#f0f6fb" : "#fff"
                                    }}
                                  >
                                    {isChecked ? (
                                      <span style={{
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                        width: "16px", height: "16px", borderRadius: "50%", flexShrink: 0,
                                        backgroundColor: color, color: "#fff", fontSize: "10px", fontWeight: 700
                                      }}>{order + 1}</span>
                                    ) : (
                                      <input type="checkbox" checked={false} disabled={isDisabled} readOnly style={{ margin: 0 }} />
                                    )}
                                    <span style={{ fontSize: "12px" }}>{job.jobCode}</span>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ padding: "8px", color: "#888" }}>No jobs found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date Range */}
                  <div className="mail-date-filter">
                    <label className="mail-date-filter-label">Date Range</label>
                    <div className="">
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!trendFilters.fromDate ? 'empty' : ''}`}
                          value={trendFilters.fromDate}
                          onChange={(e) => {
                            const newFromDate = e.target.value;
                            if (trendFilters.toDate && newFromDate > trendFilters.toDate) return;
                            setTrendFilters((prev) => ({ ...prev, fromDate: newFromDate }));
                          }}
                          max={trendFilters.toDate || undefined}
                          title="Start Date"
                        />
                      </div>
                      <span className="arrow-icon">→</span>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          className={`mail-date-input ${!trendFilters.toDate ? 'empty' : ''}`}
                          value={trendFilters.toDate}
                          onChange={(e) => {
                            const newToDate = e.target.value;
                            if (trendFilters.fromDate && newToDate < trendFilters.fromDate) return;
                            setTrendFilters((prev) => ({ ...prev, toDate: newToDate }));
                          }}
                          min={trendFilters.fromDate || undefined}
                          title="End Date"
                        />
                      </div>
                    </div>
                  </div>

                  {/* List — single select (from ListNameDropdown API) */}
                  <div className="report-filter-field" style={{ width: '190px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '0px' }}>
                      List
                    </label>
                    <select
                      style={styles.input}
                      value={trendFilters.list || ''}
                      onChange={handleTrendListChange}
                    >
                      <option value="">All</option>
                      {listNameOptions.map((l) => (
                        <option key={l.listMasterID} value={l.listName}>{l.listName}</option>
                      ))}
                    </select>
                  </div>

                  {/* Select (list_sel) — single select (from ListSegment API, depends on List) */}
                  <div className="report-filter-field" style={{ width: '190px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '0px' }}>
                      Select
                    </label>
                    <select
                      style={styles.input}
                      value={trendFilters.listSel || ''}
                      onChange={(e) => setTrendFilters((prev) => ({ ...prev, listSel: e.target.value }))}
                      disabled={!trendFilters.list || listSegmentLoading}
                    >
                      <option value="">{listSegmentLoading ? 'Loading...' : 'All'}</option>
                      {listSegmentOptions.map((s) => (
                        <option key={s.listID} value={s.listSegment}>{s.listSegment}</option>
                      ))}
                    </select>
                  </div>

                  {/* Choose Columns */}
                  <div className="report-filter-field" style={{ position: 'relative', width: '190px' }}>
                    <label style={{ color: '#000', display: 'block', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                      &nbsp;
                    </label>
                    <div ref={columnsPopupRef} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setShowColumnsPopup((v) => !v)}
                        className={`vt-choose-columns-btn${showColumnsPopup ? ' active' : ''}`}
                      >
                        Choose Columns
                        <span className="vt-choose-columns-badge">{selectedTrendColumnsCount}</span>
                      </button>

                      {showColumnsPopup && (
                        <div className="vt-columns-popup">
                          <div className="vt-columns-popup-header">
                            <div>
                              <div className="vt-columns-popup-title">Choose Columns</div>
                              <div className="vt-columns-popup-sub">Pick the metrics to compare per job</div>
                            </div>
                            <button
                              type="button"
                              className="vt-columns-popup-close"
                              onClick={() => setShowColumnsPopup(false)}
                              aria-label="Close"
                            >×</button>
                          </div>

                          <div className="vt-columns-popup-list">
                            {TREND_METRIC_OPTIONS.map((metric) => (
                              <label key={metric.key} className="vt-columns-popup-row">
                                <input
                                  type="checkbox"
                                  checked={!!selectedTrendColumns[metric.key]}
                                  onChange={() =>
                                    setSelectedTrendColumns((prev) => ({ ...prev, [metric.key]: !prev[metric.key] }))
                                  }
                                />
                                <span className="vt-metric-dot" style={{ backgroundColor: TREND_METRIC_DOT_COLOR[metric.type] }} />
                                {metric.label}
                              </label>
                            ))}
                          </div>

                          <div className="vt-columns-popup-footer">
                            <div>
                              <button type="button" className="vt-columns-popup-link" onClick={handleSelectAllTrendColumns}>Select All</button>
                              <span style={{ color: '#d7dee5', margin: '0 6px' }}>|</span>
                              <button type="button" className="vt-columns-popup-link" onClick={handleClearAllTrendColumns}>Clear All</button>
                            </div>
                            <button type="button" className="vt-columns-popup-done" onClick={() => setShowColumnsPopup(false)}>Done</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="report-filter-buttons">
                  {(() => {
                    const hasFilters =
                      selectedClients.length > 0 ||
                      selectedTrendJobIds.length > 0 ||
                      !!trendFilters.fromDate ||
                      !!trendFilters.toDate ||
                      !!trendFilters.list ||
                      !!trendFilters.listSel;
                    return (
                      <>
                        <button
                          className="report-btn-run"
                          onClick={handleFetchTrendAnalysis}
                          disabled={!hasFilters || trendLoading}
                          style={(!hasFilters || trendLoading) ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                        >
                          {trendLoading ? "Loading..." : "Run Report"}
                        </button>
                        <button className="report-btn-clear" onClick={handleClearTrendAnalysisFilters} disabled={!hasFilters} style={!hasFilters ? { opacity: 0.5, cursor: "not-allowed" } : undefined}>Clear</button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ✅ NEW: Trend Analysis — quick at-a-glance KPI cards, one per compared job */}
      {isTrendAnalysis && selectedTrendJobIds.length > 0 && (
        <div style={{ marginTop: '20px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {selectedTrendJobIds.map((jobId, idx) => {
            const color = TREND_JOB_ACCENT_COLORS[idx % TREND_JOB_ACCENT_COLORS.length];
            const jobTotals = trendTotals?.[jobId] || {};
            return (
              <div key={jobId} className="vt-kpi-card" style={{ borderTopColor: color, flex: '1 1 220px', minWidth: '220px' }}>
                <div className="vt-kpi-card-header">
                  <span className="vt-kpi-card-badge" style={{ backgroundColor: color }}>{idx + 1}</span>
                  <span className="vt-kpi-card-title">{jobLabelMap[jobId] || jobId}</span>
                </div>
                <div className="vt-kpi-card-body">
                  <div className="vt-kpi-stat">
                    <span className="vt-kpi-stat-label">Total Mailed</span>
                    <span className="vt-kpi-stat-value">{formatTrendMetricValue(jobTotals.totalMailed, 'number') || '—'}</span>
                  </div>
                  <div className="vt-kpi-stat">
                    <span className="vt-kpi-stat-label">Response Rate</span>
                    <span className="vt-kpi-stat-value">{formatTrendMetricValue(jobTotals.responseRate, 'percent') || '—'}</span>
                  </div>
                  <div className="vt-kpi-stat">
                    <span className="vt-kpi-stat-label">Income</span>
                    <span className="vt-kpi-stat-value">{formatTrendMetricValue(jobTotals.income, 'currency') || '—'}</span>
                  </div>
                  <div className="vt-kpi-stat">
                    <span className="vt-kpi-stat-label">Net</span>
                    <span className="vt-kpi-stat-value">{formatTrendMetricValue(jobTotals.net, 'currency') || '—'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ NEW: Trend Analysis empty state — shown until the user picks at least one job */}
      {isTrendAnalysis && selectedTrendJobIds.length === 0 ? (
        <div className="vt-empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
            fill="none" stroke="#c3ccd6" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
            style={{ margin: '0 auto' }}>
            <path d="M3 3v18h18" />
            <rect x="7" y="12" width="3" height="6" />
            <rect x="12.5" y="8" width="3" height="10" />
            <rect x="18" y="5" width="3" height="13" />
          </svg>
          <div className="vt-empty-state-title">Pick jobs to compare</div>
          <div className="vt-empty-state-sub">
            Select up to {MAX_TREND_JOBS} Job IDs above, then run the report to see a side-by-side comparison.
          </div>
        </div>
      ) : (
        <>
          {/* ✅ EXPORT MENU BUTTON + GRID — shared by all four report types */}
          <>
            {/* ✅ EXPORT MENU BUTTON - ADDED */}
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', marginRight: '20px' }}>
              <div style={{ position: 'relative' }} ref={menuDropdownRef}>
                <button className="job-details-menu-button" onClick={toggleMenuDropdown}>
                  <span className="menu-dot"></span>
                  <span className="menu-dot"></span>
                  <span className="menu-dot"></span>
                </button>

                {showMenuDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    minWidth: '200px',
                    overflow: 'hidden'
                  }}>
                    {/* Export to Excel */}
                    <div
                      onClick={exportToExcel}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s',
                        fontSize: '14px',
                        color: '#333',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="8" y1="13" x2="16" y2="13" />
                        <line x1="8" y1="17" x2="16" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                      Export to Excel
                    </div>

                    {/* ✅ NEW: Print To Pdf */}
                    <div
                      onClick={printToPDF}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background-color 0.2s',
                        fontSize: '14px',
                        color: '#333',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 6 2 18 2 18 9" />
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                        <rect x="6" y="14" width="12" height="8" />
                      </svg>
                      Print To PDF
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Grid */}
            <div
              className="vt-grid-card"
              style={{
                marginTop: "20px",
                '--ag-header-background-color': '#2E6F9F',
                '--ag-header-foreground-color': '#ffffff',
                '--ag-odd-row-background-color': '#f7fafc',
                '--ag-row-hover-color': '#eaf3fb',
                '--ag-font-size': '13px',
              }}
            >
              <AgGrid
                key={volumeTrackingType}
                ref={agGridRef}
                headerHeight={isTrendAnalysis ? 58 : 48}
                enableSorting={true}
                enableFilter={true}
                pagination={false}
                height="500px"
                quickFilterText={searchText}
                hideExportButton={true}
                hideSideBar={true}
                onQuickFilterChange={setSearchText}
                rowData={currentRowData}
                columnDefs={
                  isClientResults
                    ? getClientResultsColumnDefs(mailCodeCellRenderer)
                    : isDailyRevenue
                      ? getDailyRevenueColumnDefs()
                      : isTrendAnalysis
                        ? trendColumnDefs
                        : getMonthlyMailVolumeColumnDefs(jobIdCellRenderer)
                }
                onGridReady={(params) => {
                  // ✅ NEW: capture the live grid api so exports can read
                  // checked/selected rows (getExportRows above), same
                  // pattern as JobReportsTab's / ListAnalysisTab's gridApiRef.
                  gridApiRef.current = params.api;
                  if (typeof onGridReady === 'function') onGridReady(params);
                }}
                onFilterChanged={handleFilterChanged}
                pinnedBottomRowData={[isTrendAnalysis ? trendTotalsRow : totalsRow]}
                gridOptions={{
                  // ✅ Matches JobReportsTab's tooltip timing exactly.
                  tooltipShowDelay: 0,
                  tooltipHideDelay: 1000,

                  // ✅ Row-selection checkboxes + "Select All" header
                  // checkbox — identical setup to JobReportsTab. The
                  // pinned totals row stays excluded from selection either way.
                  suppressRowClickSelection: true,
                  rowSelection: 'multiple',
                  isRowSelectable: (params) => !params.data || !params.data.isTotalsRow,

                  overlayNoRowsTemplate: `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      text-align: center;
    ">

      <div style="font-size: 48px; margin-bottom: 16px; opacity: 0.5;">
        📋
      </div>

      <div style="
        font-size: 16px;
        color: #6c757d;
        font-weight: 500;
      ">
        No records found
      </div>

    </div>
      `,
                  getRowStyle: (params) => {
                    if (params.node.rowPinned === "bottom") {
                      return { fontWeight: "bold", backgroundColor: "#f0f8ff" };
                    }
                  },
                }}
                frameworkComponents={{
                  customTooltip: CustomTooltip
                }}
              />
            </div>
          </>
        </>
      )}
    </>
  );
};

export default VolumeTrackingTab;